// @ts-nocheck
// =================================================================
// PAGE 5 - FINAL CORRECT VERSION - PART 1 OF 4
// =================================================================
// IMPORTS, CONSTANTS, AND HELPER FUNCTIONS
// =================================================================
// ✅ ALL FIXES INCLUDED:
// 1. Single Skipper Signature (removed duplicate)
// 2. Employee Login Box with locks
// 3. Payment Authorization WITHOUT signature box
// 4. Employee signature ULTRA FIX with 3 attempts
// =================================================================

import React, { useState, useRef, useEffect, useContext } from "react";
import { generateLuxuryPDF } from './utils/LuxuryPDFGenerator';
import { loadPdfFonts } from './loadPdfFonts';
import { sendCheckInEmail, sendCheckOutEmail } from './services/emailService';
import authService, { getVATRate } from './authService';
import { saveBooking, getBooking, savePage5DataHybrid, getPage5DataHybrid, getAllBookings, getPage1DataHybrid, getPage2DataHybrid, getPage3DataHybrid, getPage4DataHybrid , savePage5Agreement, getPage5Agreement, getMasterItems, getMasterSections } from './services/apiService';
import { getPageMedia, mergeMediaIntoItems } from './utils/mediaStorage';
import { DataContext } from './App';
import DynamicBox from './components/DynamicBox';
import { useNavigate } from 'react-router-dom';
import { useSignatureTouch } from './utils/useSignatureTouch';
import { isCharterFullyLocked } from './utils/charterWindow';
import { countPage, buildRules, vesselKeySet, sectionLabel, pageLabel } from './utils/checkinCompleteness';
import { getVessels, getYachtItems } from './services/apiService';

import {
  brand,
  I18N,
  uid,
  compressSignature,
  compressImageWithLogging,
  saveBookingData,
  loadBookingData,
  BookingInfoBox,
  SHOW_BOOKING_INFO_ON_CHECKLIST_PAGES,
  PageHeader,
  TopControls,
  ModeDisplay,
  ActionButtons
} from './shared-components';

// =================================================================
// CONSTANTS
// =================================================================

// EMPLOYEE_CODES removed - now using authService

// EmailJS Config moved to emailService.ts

import { ITEM_LABELS as ITEM_LABELS_P2 } from "./page2-with-vessel-floorplans";
import { ITEM_LABELS as ITEM_LABELS_P3 } from "./vessel-checkin-page3";
import { ITEM_LABELS as ITEM_LABELS_P4 } from "./vessel-checkin-page4";

// Merged ITEM_LABELS from all pages (10 languages)
const ITEM_LABELS: Record<string, Record<string, string>> = { ...ITEM_LABELS_P2, ...ITEM_LABELS_P3, ...ITEM_LABELS_P4 };

const Page5_I18N = {
  en: {
    ...I18N.en,
    mainEquipment: "Main Equipment",
    hull: "Hull",
    mandatoryReading: "🔴 MANDATORY READING – CLICK HERE!",
    skipperSignatureNote: "* The skipper signature below covers this authorization",
    cancelBtn: "Cancel",
    pageTitle: "CHECK-IN/OUT COMPLETION",
    checkInMode: "✅ Check-in Mode",
    checkOutMode: "🚪 Check-out Mode",
    termsTitle: "Terms & Conditions",
    termsText: "I have read and agree to the check-in ",
    termsLink: "terms and conditions",
    privacyTitle: "Privacy Policy Consent",
    privacyText: "I agree with the ",
    privacyLink: "privacy policy & consent",
    privacyText2: " to give my personal details for use by Tailwind Yachting.",
    returnTitle: "Return Condition Acknowledgement",
    returnText: "I confirm the yacht will be returned with all listed equipment. Any damages or missing items may be charged according to the price list.",
    warningTitle: "⚠️ IMPORTANT NOTICE - MANDATORY READING",
    warningCollapsed: "⚠️ IMPORTANT - CLICK TO READ (MANDATORY)",
    warningTextPart1: "If check-in is completed by the company's specialized staff, signed by the customer and the check-in manager, and no damage or clogging is detected on the yacht ",
    warningTextHighlight: "(if there is any damage, the base manager is obliged to report it so that the customer knows, writes it in the comments and takes a photo)",
    warningTextPart2: " or toilet clogging, the company and the base have no responsibility after check-in.",
    warningTextPart3: "Upon return, the customer must pay for any damage without any excuse. The customer is responsible for any damage that occurs after check-in. They must take care of the yacht and return it in the condition they received it.",
    warningTextPart4: "Thank you in advance.",
    warningAccept: "✓ I have read and understand",
    paymentAuthTitle: "Payment Authorization",
    paymentAuthText: "The customer authorizes us to charge the pre-authorized amount on their card for any damages incurred.",
    paymentAuthAccept: "✓ I authorize payment",
    cardReleaseTitle: "Card Release",
    cardReleaseText: "No damage was found on the yacht. The customer consents to the release of the pre-authorization held on their card.",
    cardReleaseAccept: "✓ No damage found — release card",
    completeInventory: "COMPLETE INVENTORY",
    damageInventory: "DAMAGE REPORT",
    damageRate: "Rate (if damaged)",
    unitPrice: "Unit Price",
    totalPrice: "Total",
    netTotal: "NET TOTAL",
    vatPercent: "VAT",
    totalWithVAT: "TOTAL WITH VAT",
    notesTitle: "Additional Remarks",
    notesPlaceholder: "Write any remarks below and inform our base staff...",
    skipperSignatureTitle: "Skipper's Signature",
    employeeSignatureTitle: "Employee's Signature",
    clear: "Clear",
    signatureRequired: "Signature required",
    damagePhotosTitle: "Damage Photos",
    mustBeRead: "MUST BE READ!",
    damagePhotosBtn: "📸 DAMAGE PHOTOS",
    photosBtn: "📸 PHOTOS",
    loggedIn: "Logged in",
    logoutBtn: "Logout",
    employeeCodeRequired: "⚠️ Employee Code Required",
    employeeCodeNeeded: "⚠️ Employee code required!",
    signatureRequired: "Signature required",
    save: "Save draft",
    clear: "Clear",
    pdf: "PDF",
    back: "Back",
    submit: "Submit",
    ok: "OK - I CONFIRM",
    fieldRequired: "Field required",
    emailSent: "Confirmation emails sent successfully!",
    emailError: "Error sending email. Please try again.",
    checkInComplete: "Check-in completed successfully!",
    pdfGenerated: "PDF generated successfully!",
    dataSaved: "Data saved automatically!",
    footerAddress: "Leukosias 37, Alimos",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Tel: +30 6978196009"
  },
  el: {
    ...I18N.el,
    mainEquipment: "Κύριος Εξοπλισμός",
    hull: "Κύτος",
    mandatoryReading: "🔴 ΥΠΟΧΡΕΩΤΙΚΗ ΑΝΑΓΝΩΣΗ – ΚΛΙΚ ΕΔΩ!",
    skipperSignatureNote: "* Η υπογραφή του κυβερνήτη παρακάτω καλύπτει και αυτήν την εξουσιοδότηση",
    cancelBtn: "Ακύρωση",
    pageTitle: "ΟΛΟΚΛΗΡΩΣΗ CHECK-IN/OUT",
    checkInMode: "✅ Λειτουργία Check-in",
    checkOutMode: "🚪 Λειτουργία Check-out",
    termsTitle: "Όροι & Προϋποθέσεις",
    termsText: "Έχω διαβάσει και συμφωνώ με τους ",
    termsLink: "όρους και προϋποθέσεις",
    privacyTitle: "Συγκατάθεση Πολιτικής Απορρήτου",
    privacyText: "Συμφωνώ με την ",
    privacyLink: "πολιτική απορρήτου & συναίνεση",
    privacyText2: " και συναινώ στη χρήση των προσωπικών μου στοιχείων από την Tailwind Yachting.",
    returnTitle: "Αναγνώριση Κατάστασης Επιστροφής",
    returnText: "Επιβεβαιώνω ότι το σκάφος θα επιστραφεί με όλον τον καταγεγραμμένο εξοπλισμό. Τυχόν ζημιές ή ελλείψεις μπορεί να χρεωθούν σύμφωνα με τον τιμοκατάλογο.",
    warningTitle: "⚠️ ΣΗΜΑΝΤΙΚΗ ΕΙΔΟΠΟΙΗΣΗ - ΥΠΟΧΡΕΩΤΙΚΗ ΑΝΑΓΝΩΣΗ",
    warningCollapsed: "⚠️ ΣΗΜΑΝΤΙΚΟ - ΚΛΙΚ ΓΙΑ ΑΝΑΓΝΩΣΗ (ΥΠΟΧΡΕΩΤΙΚΟ)",
    warningTextPart1: "Εάν γίνει check-in από το εξειδικευμένο προσωπικό της εταιρίας, υπογράψει ο πελάτης και ο υπεύθυνος του check-in, και δεν διαπιστωθεί καμία ζημιά στο σκάφος ",
    warningTextHighlight: "(εάν υπάρχει κάποια ζημιά υποχρεούται να το πει ο υπεύθυνος της βάσης ώστε ο πελάτης να το γνωρίζει, να το γράψει στα σχόλια και να βγάλει φωτογραφία)",
    warningTextPart2: " ή βούλωμα στην τουαλέτα, η εταιρία και η βάση δεν έχουν καμία ευθύνη μετά το check-in.",
    warningTextPart3: "Ο πελάτης στην επιστροφή θα πρέπει να πληρώσει την ζημιά χωρίς καμία δικαιολογία. Ο πελάτης είναι υπεύθυνος για οποιαδήποτε ζημιά γίνει μετά το check-in. Θα πρέπει να φροντίζει το σκάφος και να το παραδώσει στην κατάσταση που το πήρε.",
    warningTextPart4: "Ευχαριστούμε εκ των προτέρων.",
    warningAccept: "✓ Έχω διαβάσει και κατανοώ",
    paymentAuthTitle: "Εξουσιοδότηση Πληρωμής",
    paymentAuthText: "Ο πελάτης με την συναίνεσή του μας επιτρέπει να πάρουμε χρήματα από την προεγγραφή που έχει γίνει στην κάρτα του για ζημιές που έχει κάνει.",
    paymentAuthAccept: "✓ Εξουσιοδοτώ την πληρωμή",
    cardReleaseTitle: "Αποδέσμευση Κάρτας",
    cardReleaseText: "Δεν βρέθηκαν ζημιές στο σκάφος. Ο πελάτης συναινεί στην αποδέσμευση της προεγγραφής (pre-authorization) που έχει γίνει στην κάρτα του.",
    cardReleaseAccept: "✓ Δεν βρέθηκαν ζημιές — αποδέσμευση κάρτας",
    completeInventory: "ΠΛΗΡΗΣ ΑΠΟΓΡΑΦΗ",
    damageInventory: "ΑΝΑΦΟΡΑ ΖΗΜΙΩΝ",
    damageRate: "Τιμή (αν καταστραφεί)",
    unitPrice: "Τιμή Μονάδας",
    totalPrice: "Σύνολο",
    netTotal: "ΚΑΘΑΡΟ ΣΥΝΟΛΟ",
    vatPercent: "ΦΠΑ",
    totalWithVAT: "ΣΥΝΟΛΟ ΜΕ ΦΠΑ",
    notesTitle: "Επιπλέον Παρατηρήσεις",
    notesPlaceholder: "Γράψτε παρατηρήσεις και ενημερώστε το προσωπικό της βάσης...",
    skipperSignatureTitle: "Υπογραφή Κυβερνήτη",
    employeeSignatureTitle: "Υπογραφή Υπαλλήλου",
    employeeCodeRequired: "⚠️ Απαιτείται Κωδικός Υπαλλήλου",
    employeeCodeNeeded: "⚠️ Χρειάζεται κωδικός υπαλλήλου!",
    signatureRequired: "Υποχρεωτική υπογραφή",
    save: "Αποθήκευση",
    clear: "Καθαρισμός",
    pdf: "PDF",
    back: "Πίσω",
    submit: "Υποβολή",
    ok: "OK - ΕΠΙΒΕΒΑΙΩΝΩ",
    fieldRequired: "Υποχρεωτικό πεδίο",
    emailSent: "Τα emails επιβεβαίωσης στάλθηκαν επιτυχώς!",
    emailError: "Σφάλμα αποστολής email. Παρακαλώ δοκιμάστε ξανά.",
    checkInComplete: "Το Check-in ολοκληρώθηκε επιτυχώς!",
    pdfGenerated: "Το PDF δημιουργήθηκε επιτυχώς!",
    dataSaved: "Τα δεδομένα αποθηκεύτηκαν αυτόματα!",
    footerAddress: "Λευκωσίας 37, Άλιμος",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Τηλ: +30 6978196009"
  },
  it: {
    ...I18N.it,
    mainEquipment: "Equipaggiamento Principale",
    hull: "Scafo",
    mandatoryReading: "🔴 LETTURA OBBLIGATORIA – CLICCA QUI!",
    skipperSignatureNote: "* La firma dello skipper qui sotto copre anche questa autorizzazione",
    cancelBtn: "Annulla",
    pageTitle: "COMPLETAMENTO CHECK-IN/OUT",
    checkInMode: "\u2705 Modalit\u00e0 Check-in",
    checkOutMode: "\ud83d\udce6 Modalit\u00e0 Check-out",
    termsTitle: "Termini e Condizioni",
    privacyTitle: "Consenso alla Privacy",
    privacyText: "Acconsento alla ",
    privacyText2: " e all'utilizzo dei miei dati personali da parte di Tailwind Yachting.",
    returnTitle: "Riconoscimento Condizioni di Restituzione",
    returnText: "Confermo che lo yacht sarà restituito con tutta l'attrezzatura elencata. Eventuali danni o oggetti mancanti potranno essere addebitati secondo il listino prezzi.",
    termsText: "Accetto i termini e le condizioni del noleggio. Confermo che tutte le informazioni fornite sono corrette e che ho ispezionato l'imbarcazione.",
    warningTitle: "\u26a0\ufe0f Avviso Importante",
    warningTextPart1: "ATTENZIONE: Eventuali danni, perdite o malfunzionamenti NON registrati nel presente documento saranno considerati avvenuti durante il periodo di noleggio.",
    warningTextPart2: "Il noleggiatore sar\u00e0 responsabile per tutti i costi di riparazione o sostituzione.",
    warningTextPart3: "Si prega di ispezionare accuratamente e segnalare TUTTO prima della partenza.",
    warningTextPart4: "Grazie in anticipo.",
    warningAccept: "\u2714 Ho letto e compreso",
    paymentAuthTitle: "Autorizzazione al Pagamento",
    paymentAuthText: "Il cliente con il suo consenso ci autorizza a prelevare dalla carta di credito registrata i costi per eventuali danni riscontrati.",
    paymentAuthAccept: "\u2714 Autorizzo il pagamento",
    cardReleaseTitle: "Svincolo della Carta",
    cardReleaseText: "Non sono stati riscontrati danni sull'imbarcazione. Il cliente acconsente allo svincolo della pre-autorizzazione registrata sulla sua carta.",
    cardReleaseAccept: "\u2714 Nessun danno riscontrato \u2014 svincola la carta",
    completeInventory: "INVENTARIO COMPLETO",
    damageInventory: "RAPPORTO DANNI",
    damageRate: "Costo (se danneggiato)",
    unitPrice: "Prezzo Unitario",
    totalPrice: "Totale",
    netTotal: "TOTALE NETTO",
    vatPercent: "IVA",
    totalWithVAT: "TOTALE CON IVA",
    notesTitle: "Note Aggiuntive",
    notesPlaceholder: "Scrivere osservazioni e informare il personale della base...",
    skipperSignatureTitle: "Firma dello Skipper",
    employeeSignatureTitle: "Firma del Dipendente",
    employeeCodeRequired: "\u26a0 Codice Dipendente Richiesto",
    employeeCodeNeeded: "\u26a0 Codice dipendente necessario!",
    signatureRequired: "Firma obbligatoria",
    save: "Salva bozza",
    clear: "Cancella",
    pdf: "PDF",
    back: "Indietro",
    submit: "Invia",
    ok: "OK - CONFERMO",
    fieldRequired: "Campo obbligatorio",
    emailSent: "Email di conferma inviate con successo!",
    emailError: "Errore nell'invio email. Riprovare.",
    checkInComplete: "Check-in completato con successo!",
    pdfGenerated: "PDF generato con successo!",
    dataSaved: "Dati salvati automaticamente!",
    footerAddress: "Leukosias 37, Alimos",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Tel: +30 6978196009"
  },
  de: {
    ...I18N.de,
    mainEquipment: "Hauptausrüstung",
    hull: "Rumpf",
    mandatoryReading: "🔴 PFLICHTLEKTÜRE – HIER KLICKEN!",
    skipperSignatureNote: "* Die Unterschrift des Skippers unten deckt auch diese Genehmigung ab",
    cancelBtn: "Abbrechen",
    pageTitle: "CHECK-IN/OUT ABSCHLUSS",
    checkInMode: "\u2705 Check-in Modus",
    checkOutMode: "\ud83d\udce6 Check-out Modus",
    termsTitle: "Allgemeine Gesch\u00e4ftsbedingungen",
    privacyTitle: "Datenschutz-Einwilligung",
    privacyText: "Ich stimme der ",
    privacyText2: " zu und erkläre mich mit der Nutzung meiner persönlichen Daten durch Tailwind Yachting einverstanden.",
    returnTitle: "Bestätigung des Rückgabezustands",
    returnText: "Ich bestätige, dass die Yacht mit der gesamten aufgelisteten Ausrüstung zurückgegeben wird. Eventuelle Schäden oder fehlende Gegenstände können gemäß der Preisliste berechnet werden.",
    termsText: "Ich akzeptiere die Mietbedingungen. Ich best\u00e4tige, dass alle Angaben korrekt sind und ich das Boot inspiziert habe.",
    warningTitle: "\u26a0\ufe0f Wichtiger Hinweis",
    warningTextPart1: "ACHTUNG: Sch\u00e4den, Verluste oder Fehlfunktionen, die NICHT in diesem Dokument erfasst sind, gelten als w\u00e4hrend der Mietzeit entstanden.",
    warningTextPart2: "Der Mieter haftet f\u00fcr alle Reparatur- oder Ersatzkosten.",
    warningTextPart3: "Bitte inspizieren Sie gr\u00fcndlich und melden Sie ALLES vor der Abfahrt.",
    warningTextPart4: "Vielen Dank im Voraus.",
    warningAccept: "\u2714 Ich habe gelesen und verstanden",
    paymentAuthTitle: "Zahlungsautorisierung",
    paymentAuthText: "Der Kunde autorisiert uns, Kosten f\u00fcr festgestellte Sch\u00e4den von der hinterlegten Kreditkarte abzubuchen.",
    paymentAuthAccept: "\u2714 Ich autorisiere die Zahlung",
    cardReleaseTitle: "Kartenfreigabe",
    cardReleaseText: "Am Boot wurden keine Sch\u00e4den festgestellt. Der Kunde stimmt der Freigabe der auf seiner Kreditkarte hinterlegten Vorautorisierung zu.",
    cardReleaseAccept: "\u2714 Keine Sch\u00e4den festgestellt \u2014 Karte freigeben",
    completeInventory: "VOLLST\u00c4NDIGES INVENTAR",
    damageInventory: "SCHADENSBERICHT",
    damageRate: "Kosten (bei Besch\u00e4digung)",
    unitPrice: "St\u00fcckpreis",
    totalPrice: "Gesamt",
    netTotal: "NETTOBETRAG",
    vatPercent: "MwSt",
    totalWithVAT: "GESAMT MIT MwSt",
    notesTitle: "Zus\u00e4tzliche Anmerkungen",
    notesPlaceholder: "Anmerkungen schreiben und Basispersonal informieren...",
    skipperSignatureTitle: "Unterschrift des Skippers",
    employeeSignatureTitle: "Unterschrift des Mitarbeiters",
    employeeCodeRequired: "\u26a0 Mitarbeitercode erforderlich",
    employeeCodeNeeded: "\u26a0 Mitarbeitercode ben\u00f6tigt!",
    signatureRequired: "Unterschrift erforderlich",
    save: "Entwurf speichern",
    clear: "L\u00f6schen",
    pdf: "PDF",
    back: "Zur\u00fcck",
    submit: "Absenden",
    ok: "OK - ICH BEST\u00c4TIGE",
    fieldRequired: "Pflichtfeld",
    emailSent: "Best\u00e4tigungs-E-Mails erfolgreich gesendet!",
    emailError: "Fehler beim E-Mail-Versand. Bitte erneut versuchen.",
    checkInComplete: "Check-in erfolgreich abgeschlossen!",
    pdfGenerated: "PDF erfolgreich erstellt!",
    dataSaved: "Daten automatisch gespeichert!",
    footerAddress: "Leukosias 37, Alimos",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Tel: +30 6978196009"
  },
  ru: {
    ...I18N.ru,
    mainEquipment: "Основное оборудование",
    hull: "Корпус",
    mandatoryReading: "🔴 ОБЯЗАТЕЛЬНО К ПРОЧТЕНИЮ – НАЖМИТЕ ЗДЕСЬ!",
    skipperSignatureNote: "* Подпись шкипера ниже также покрывает данное разрешение",
    cancelBtn: "Отмена",
    pageTitle: "\u0417\u0410\u0412\u0415\u0420\u0428\u0415\u041d\u0418\u0415 CHECK-IN/OUT",
    checkInMode: "\u2705 \u0420\u0435\u0436\u0438\u043c Check-in",
    checkOutMode: "\ud83d\udce6 \u0420\u0435\u0436\u0438\u043c Check-out",
    termsTitle: "\u0423\u0441\u043b\u043e\u0432\u0438\u044f",
    privacyTitle: "Согласие на обработку данных",
    privacyText: "Я соглашаюсь с ",
    privacyText2: " и даю согласие на использование моих персональных данных компанией Tailwind Yachting.",
    returnTitle: "Подтверждение состояния возврата",
    returnText: "Я подтверждаю, что яхта будет возвращена со всем перечисленным оборудованием. Любые повреждения или недостающие предметы могут быть оплачены согласно прейскуранту.",
    termsText: "\u042f \u043f\u0440\u0438\u043d\u0438\u043c\u0430\u044e \u0443\u0441\u043b\u043e\u0432\u0438\u044f \u0430\u0440\u0435\u043d\u0434\u044b. \u041f\u043e\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0430\u044e, \u0447\u0442\u043e \u0432\u0441\u044f \u0438\u043d\u0444\u043e\u0440\u043c\u0430\u0446\u0438\u044f \u0432\u0435\u0440\u043d\u0430 \u0438 \u044f\u0445\u0442\u0430 \u043e\u0441\u043c\u043e\u0442\u0440\u0435\u043d\u0430.",
    warningTitle: "\u26a0\ufe0f \u0412\u0430\u0436\u043d\u043e\u0435 \u043f\u0440\u0435\u0434\u0443\u043f\u0440\u0435\u0436\u0434\u0435\u043d\u0438\u0435",
    warningTextPart1: "\u0412\u041d\u0418\u041c\u0410\u041d\u0418\u0415: \u041f\u043e\u0432\u0440\u0435\u0436\u0434\u0435\u043d\u0438\u044f, \u043d\u0435 \u0437\u0430\u0444\u0438\u043a\u0441\u0438\u0440\u043e\u0432\u0430\u043d\u043d\u044b\u0435 \u0432 \u0434\u043e\u043a\u0443\u043c\u0435\u043d\u0442\u0435, \u0441\u0447\u0438\u0442\u0430\u044e\u0442\u0441\u044f \u0432\u043e\u0437\u043d\u0438\u043a\u0448\u0438\u043c\u0438 \u0432 \u043f\u0435\u0440\u0438\u043e\u0434 \u0430\u0440\u0435\u043d\u0434\u044b.",
    warningTextPart2: "\u0410\u0440\u0435\u043d\u0434\u0430\u0442\u043e\u0440 \u043d\u0435\u0441\u0451\u0442 \u043e\u0442\u0432\u0435\u0442\u0441\u0442\u0432\u0435\u043d\u043d\u043e\u0441\u0442\u044c \u0437\u0430 \u0432\u0441\u0435 \u0440\u0430\u0441\u0445\u043e\u0434\u044b.",
    warningTextPart3: "\u041f\u0440\u043e\u0432\u0435\u0440\u044c\u0442\u0435 \u0432\u0441\u0451 \u0442\u0449\u0430\u0442\u0435\u043b\u044c\u043d\u043e \u043f\u0435\u0440\u0435\u0434 \u043e\u0442\u043f\u043b\u044b\u0442\u0438\u0435\u043c.",
    warningTextPart4: "\u0421\u043f\u0430\u0441\u0438\u0431\u043e.",
    warningAccept: "\u2714 \u041f\u0440\u043e\u0447\u0438\u0442\u0430\u043d\u043e \u0438 \u043f\u043e\u043d\u044f\u0442\u043e",
    paymentAuthTitle: "\u0410\u0432\u0442\u043e\u0440\u0438\u0437\u0430\u0446\u0438\u044f \u043e\u043f\u043b\u0430\u0442\u044b",
    paymentAuthText: "\u041a\u043b\u0438\u0435\u043d\u0442 \u0440\u0430\u0437\u0440\u0435\u0448\u0430\u0435\u0442 \u0441\u043f\u0438\u0441\u0430\u043d\u0438\u0435 \u0441\u0440\u0435\u0434\u0441\u0442\u0432 \u0437\u0430 \u0443\u0449\u0435\u0440\u0431 \u0441 \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043e\u0432\u0430\u043d\u043d\u043e\u0439 \u043a\u0430\u0440\u0442\u044b.",
    paymentAuthAccept: "\u2714 \u0410\u0432\u0442\u043e\u0440\u0438\u0437\u0443\u044e \u043e\u043f\u043b\u0430\u0442\u0443",
    cardReleaseTitle: "\u0420\u0430\u0437\u0431\u043b\u043e\u043a\u0438\u0440\u043e\u0432\u043a\u0430 \u043a\u0430\u0440\u0442\u044b",
    cardReleaseText: "\u041d\u0430 \u0441\u0443\u0434\u043d\u0435 \u043d\u0435 \u043e\u0431\u043d\u0430\u0440\u0443\u0436\u0435\u043d\u043e \u043f\u043e\u0432\u0440\u0435\u0436\u0434\u0435\u043d\u0438\u0439. \u041a\u043b\u0438\u0435\u043d\u0442 \u0441\u043e\u0433\u043b\u0430\u0448\u0430\u0435\u0442\u0441\u044f \u043d\u0430 \u0440\u0430\u0437\u0431\u043b\u043e\u043a\u0438\u0440\u043e\u0432\u043a\u0443 \u043f\u0440\u0435\u0434\u0430\u0432\u0442\u043e\u0440\u0438\u0437\u0430\u0446\u0438\u0438, \u0443\u0434\u0435\u0440\u0436\u0430\u043d\u043d\u043e\u0439 \u043d\u0430 \u0435\u0433\u043e \u043a\u0430\u0440\u0442\u0435.",
    cardReleaseAccept: "\u2714 \u041f\u043e\u0432\u0440\u0435\u0436\u0434\u0435\u043d\u0438\u0439 \u043d\u0435 \u043e\u0431\u043d\u0430\u0440\u0443\u0436\u0435\u043d\u043e \u2014 \u0440\u0430\u0437\u0431\u043b\u043e\u043a\u0438\u0440\u043e\u0432\u0430\u0442\u044c \u043a\u0430\u0440\u0442\u0443",
    completeInventory: "\u041f\u041e\u041b\u041d\u0410\u042f \u0418\u041d\u0412\u0415\u041d\u0422\u0410\u0420\u0418\u0417\u0410\u0426\u0418\u042f",
    damageInventory: "\u041e\u0422\u0427\u0401\u0422 \u041e \u041f\u041e\u0412\u0420\u0415\u0416\u0414\u0415\u041d\u0418\u042f\u0425",
    damageRate: "\u0421\u0442\u043e\u0438\u043c\u043e\u0441\u0442\u044c (\u043f\u0440\u0438 \u043f\u043e\u0432\u0440\u0435\u0436\u0434\u0435\u043d\u0438\u0438)",
    unitPrice: "\u0426\u0435\u043d\u0430 \u0437\u0430 \u0435\u0434.",
    totalPrice: "\u0418\u0442\u043e\u0433\u043e",
    netTotal: "\u0418\u0422\u041e\u0413\u041e \u0411\u0415\u0417 \u041d\u0414\u0421",
    vatPercent: "\u041d\u0414\u0421",
    totalWithVAT: "\u0418\u0422\u041e\u0413\u041e \u0421 \u041d\u0414\u0421",
    notesTitle: "\u0414\u043e\u043f\u043e\u043b\u043d\u0438\u0442\u0435\u043b\u044c\u043d\u044b\u0435 \u0437\u0430\u043c\u0435\u0447\u0430\u043d\u0438\u044f",
    notesPlaceholder: "\u041d\u0430\u043f\u0438\u0448\u0438\u0442\u0435 \u0437\u0430\u043c\u0435\u0447\u0430\u043d\u0438\u044f...",
    skipperSignatureTitle: "\u041f\u043e\u0434\u043f\u0438\u0441\u044c \u0448\u043a\u0438\u043f\u0435\u0440\u0430",
    employeeSignatureTitle: "\u041f\u043e\u0434\u043f\u0438\u0441\u044c \u0441\u043e\u0442\u0440\u0443\u0434\u043d\u0438\u043a\u0430",
    employeeCodeRequired: "\u26a0 \u0422\u0440\u0435\u0431\u0443\u0435\u0442\u0441\u044f \u043a\u043e\u0434 \u0441\u043e\u0442\u0440\u0443\u0434\u043d\u0438\u043a\u0430",
    employeeCodeNeeded: "\u26a0 \u041d\u0443\u0436\u0435\u043d \u043a\u043e\u0434 \u0441\u043e\u0442\u0440\u0443\u0434\u043d\u0438\u043a\u0430!",
    signatureRequired: "\u041f\u043e\u0434\u043f\u0438\u0441\u044c \u043e\u0431\u044f\u0437\u0430\u0442\u0435\u043b\u044c\u043d\u0430",
    save: "\u0421\u043e\u0445\u0440\u0430\u043d\u0438\u0442\u044c \u0447\u0435\u0440\u043d\u043e\u0432\u0438\u043a",
    clear: "\u041e\u0447\u0438\u0441\u0442\u0438\u0442\u044c",
    pdf: "PDF",
    back: "\u041d\u0430\u0437\u0430\u0434",
    submit: "\u041e\u0442\u043f\u0440\u0430\u0432\u0438\u0442\u044c",
    ok: "OK - \u041f\u041e\u0414\u0422\u0412\u0415\u0420\u0416\u0414\u0410\u042e",
    fieldRequired: "\u041e\u0431\u044f\u0437\u0430\u0442\u0435\u043b\u044c\u043d\u043e\u0435 \u043f\u043e\u043b\u0435",
    emailSent: "\u041f\u0438\u0441\u044c\u043c\u0430 \u043e\u0442\u043f\u0440\u0430\u0432\u043b\u0435\u043d\u044b!",
    emailError: "\u041e\u0448\u0438\u0431\u043a\u0430 \u043e\u0442\u043f\u0440\u0430\u0432\u043a\u0438. \u041f\u043e\u0432\u0442\u043e\u0440\u0438\u0442\u0435.",
    checkInComplete: "Check-in \u0443\u0441\u043f\u0435\u0448\u043d\u043e \u0437\u0430\u0432\u0435\u0440\u0448\u0451\u043d!",
    pdfGenerated: "PDF \u0441\u043e\u0437\u0434\u0430\u043d!",
    dataSaved: "\u0414\u0430\u043d\u043d\u044b\u0435 \u0441\u043e\u0445\u0440\u0430\u043d\u0435\u043d\u044b!",
    footerAddress: "Leukosias 37, Alimos",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Tel: +30 6978196009"
  },
  fr: {
    ...I18N.fr,
    mainEquipment: "Équipement Principal",
    hull: "Coque",
    mandatoryReading: "🔴 LECTURE OBLIGATOIRE – CLIQUEZ ICI!",
    skipperSignatureNote: "* La signature du skipper ci-dessous couvre également cette autorisation",
    cancelBtn: "Annuler",
    pageTitle: "FIN DU CHECK-IN/OUT",
    checkInMode: "\u2705 Mode Check-in",
    checkOutMode: "\ud83d\udce6 Mode Check-out",
    termsTitle: "Conditions G\u00e9n\u00e9rales",
    privacyTitle: "Consentement de Confidentialité",
    privacyText: "J'accepte la ",
    privacyText2: " et j'autorise l'utilisation de mes données personnelles par Tailwind Yachting.",
    returnTitle: "Reconnaissance de l'État de Retour",
    returnText: "Je confirme que le yacht sera restitué avec tout l'équipement listé. Tout dommage ou objet manquant pourra être facturé selon la grille tarifaire.",
    termsText: "J'accepte les conditions de location. Je confirme que toutes les informations sont correctes et que j'ai inspect\u00e9 le bateau.",
    warningTitle: "\u26a0\ufe0f Avertissement Important",
    warningTextPart1: "ATTENTION: Les dommages non enregistr\u00e9s seront consid\u00e9r\u00e9s comme survenus pendant la p\u00e9riode de location.",
    warningTextPart2: "Le locataire sera responsable de tous les co\u00fbts.",
    warningTextPart3: "Veuillez tout inspecter avant le d\u00e9part.",
    warningTextPart4: "Merci d'avance.",
    warningAccept: "\u2714 J'ai lu et compris",
    paymentAuthTitle: "Autorisation de Paiement",
    paymentAuthText: "Le client autorise le pr\u00e9l\u00e8vement sur sa carte pour les dommages constat\u00e9s.",
    paymentAuthAccept: "\u2714 J'autorise le paiement",
    cardReleaseTitle: "Lib\u00e9ration de la Carte",
    cardReleaseText: "Aucun dommage n'a \u00e9t\u00e9 constat\u00e9 sur le bateau. Le client consent \u00e0 la lib\u00e9ration de la pr\u00e9-autorisation effectu\u00e9e sur sa carte.",
    cardReleaseAccept: "\u2714 Aucun dommage constat\u00e9 \u2014 lib\u00e9rer la carte",
    completeInventory: "INVENTAIRE COMPLET",
    damageInventory: "RAPPORT DE DOMMAGES",
    damageRate: "Co\u00fbt (si endommag\u00e9)",
    unitPrice: "Prix Unitaire",
    totalPrice: "Total",
    netTotal: "TOTAL NET",
    vatPercent: "TVA",
    totalWithVAT: "TOTAL TTC",
    notesTitle: "Remarques Suppl\u00e9mentaires",
    notesPlaceholder: "\u00c9crivez vos remarques...",
    skipperSignatureTitle: "Signature du Skipper",
    employeeSignatureTitle: "Signature de l'Employ\u00e9",
    employeeCodeRequired: "\u26a0 Code Employ\u00e9 Requis",
    employeeCodeNeeded: "\u26a0 Code employ\u00e9 n\u00e9cessaire!",
    signatureRequired: "Signature obligatoire",
    save: "Enregistrer brouillon",
    clear: "Effacer",
    pdf: "PDF",
    back: "Retour",
    submit: "Soumettre",
    ok: "OK - JE CONFIRME",
    fieldRequired: "Champ obligatoire",
    emailSent: "Emails envoy\u00e9s avec succ\u00e8s!",
    emailError: "Erreur d'envoi. R\u00e9essayez.",
    checkInComplete: "Check-in termin\u00e9!",
    pdfGenerated: "PDF g\u00e9n\u00e9r\u00e9!",
    dataSaved: "Donn\u00e9es sauvegard\u00e9es!",
    footerAddress: "Leukosias 37, Alimos",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Tel: +30 6978196009"
  },
  ro: {
    ...I18N.ro,
    mainEquipment: "Echipament Principal",
    hull: "Coca",
    mandatoryReading: "🔴 LECTURĂ OBLIGATORIE – CLICK AICI!",
    skipperSignatureNote: "* Semnătura skipperului de mai jos acoperă și această autorizare",
    cancelBtn: "Anulare",
    pageTitle: "FINALIZARE CHECK-IN/OUT",
    checkInMode: "\u2705 Mod Check-in",
    checkOutMode: "\ud83d\udce6 Mod Check-out",
    termsTitle: "Termeni \u0219i Condi\u021bii",
    privacyTitle: "Consimțământ Confidențialitate",
    privacyText: "Sunt de acord cu ",
    privacyText2: " și consimt la utilizarea datelor mele personale de către Tailwind Yachting.",
    returnTitle: "Confirmarea Stării de Returnare",
    returnText: "Confirm că iahtul va fi returnat cu tot echipamentul listat. Orice daune sau obiecte lipsă pot fi taxate conform listei de prețuri.",
    termsText: "Accept termenii \u0219i condi\u021biile de \u00eenchiriere. Confirm c\u0103 toate informa\u021biile sunt corecte.",
    warningTitle: "\u26a0\ufe0f Avertisment Important",
    warningTextPart1: "ATEN\u021aIE: Daunele ne\u00eenregistrate vor fi considerate ap\u0103rute \u00een perioada de \u00eenchiriere.",
    warningTextPart2: "Chiria\u0219ul va fi responsabil pentru toate costurile.",
    warningTextPart3: "V\u0103 rug\u0103m s\u0103 inspectati totul \u00eenainte de plecare.",
    warningTextPart4: "V\u0103 mul\u021bumim.",
    warningAccept: "\u2714 Am citit \u0219i am \u00een\u021beles",
    paymentAuthTitle: "Autorizare de Plat\u0103",
    paymentAuthText: "Clientul autorizeaz\u0103 debitarea cardului pentru daune constatate.",
    paymentAuthAccept: "\u2714 Autorizez plata",
    cardReleaseTitle: "Deblocarea Cardului",
    cardReleaseText: "Nu au fost constatate daune pe ambarca\u021biune. Clientul consimte la deblocarea pre-autoriz\u0103rii re\u021binute pe cardul s\u0103u.",
    cardReleaseAccept: "\u2714 Nicio daun\u0103 constatat\u0103 \u2014 deblocheaz\u0103 cardul",
    completeInventory: "INVENTAR COMPLET",
    damageInventory: "RAPORT DAUNE",
    damageRate: "Cost (dac\u0103 deteriorat)",
    unitPrice: "Pre\u021b Unitar",
    totalPrice: "Total",
    netTotal: "TOTAL NET",
    vatPercent: "TVA",
    totalWithVAT: "TOTAL CU TVA",
    notesTitle: "Observa\u021bii Suplimentare",
    notesPlaceholder: "Scrie\u021bi observa\u021bii...",
    skipperSignatureTitle: "Semn\u0103tura Skipperului",
    employeeSignatureTitle: "Semn\u0103tura Angajatului",
    employeeCodeRequired: "\u26a0 Cod Angajat Necesar",
    employeeCodeNeeded: "\u26a0 Este necesar codul!",
    signatureRequired: "Semn\u0103tura obligatorie",
    save: "Salveaz\u0103 ciorn\u0103",
    clear: "\u0218terge",
    pdf: "PDF",
    back: "\u00cenapoi",
    submit: "Trimite",
    ok: "OK - CONFIRM",
    fieldRequired: "C\u00e2mp obligatoriu",
    emailSent: "Emailuri trimise cu succes!",
    emailError: "Eroare la trimitere. Re\u00eencerca\u021bi.",
    checkInComplete: "Check-in finalizat!",
    pdfGenerated: "PDF generat!",
    dataSaved: "Date salvate automat!",
    footerAddress: "Leukosias 37, Alimos",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Tel: +30 6978196009"
  },
  pl: {
    ...I18N.pl,
    mainEquipment: "Wyposażenie Główne",
    hull: "Kadłub",
    mandatoryReading: "🔴 LEKTURA OBOWIĄZKOWA – KLIKNIJ TUTAJ!",
    skipperSignatureNote: "* Podpis skippera poniżej obejmuje również tę autoryzację",
    cancelBtn: "Anuluj",
    pageTitle: "ZAKO\u0143CZENIE CHECK-IN/OUT",
    checkInMode: "\u2705 Tryb Check-in",
    checkOutMode: "\ud83d\udce6 Tryb Check-out",
    termsTitle: "Regulamin",
    privacyTitle: "Zgoda na Prywatność",
    privacyText: "Wyrażam zgodę na ",
    privacyText2: " i na wykorzystanie moich danych osobowych przez Tailwind Yachting.",
    returnTitle: "Potwierdzenie Stanu Zwrotu",
    returnText: "Potwierdzam, że jacht zostanie zwrócony z całym wymienionym wyposażeniem. Wszelkie uszkodzenia lub brakujące przedmioty mogą być obciążone zgodnie z cennikiem.",
    termsText: "Akceptuj\u0119 warunki wynajmu. Potwierdzam poprawno\u015b\u0107 danych i inspekcj\u0119 jachtu.",
    warningTitle: "\u26a0\ufe0f Wa\u017cne Ostrze\u017cenie",
    warningTextPart1: "UWAGA: Uszkodzenia niezg\u0142oszone b\u0119d\u0105 uznane za powsta\u0142e w okresie wynajmu.",
    warningTextPart2: "Najemca ponosi odpowiedzialno\u015b\u0107 za wszystkie koszty.",
    warningTextPart3: "Prosz\u0119 dok\u0142adnie sprawdzi\u0107 wszystko przed wyp\u0142yni\u0119ciem.",
    warningTextPart4: "Dzi\u0119kujemy.",
    warningAccept: "\u2714 Przeczyta\u0142em i rozumiem",
    paymentAuthTitle: "Autoryzacja P\u0142atno\u015bci",
    paymentAuthText: "Klient autoryzuje obci\u0105\u017cenie karty za stwierdzone szkody.",
    paymentAuthAccept: "\u2714 Autoryzuj\u0119 p\u0142atno\u015b\u0107",
    cardReleaseTitle: "Zwolnienie Karty",
    cardReleaseText: "Na jachcie nie stwierdzono \u017cadnych szk\u00f3d. Klient wyra\u017ca zgod\u0119 na zwolnienie preautoryzacji zablokowanej na jego karcie.",
    cardReleaseAccept: "\u2714 Nie stwierdzono szk\u00f3d \u2014 zwolnij kart\u0119",
    completeInventory: "PE\u0141NY INWENTARZ",
    damageInventory: "RAPORT USZKODZE\u0143",
    damageRate: "Koszt (je\u015bli uszkodzony)",
    unitPrice: "Cena Jednostkowa",
    totalPrice: "Razem",
    netTotal: "RAZEM NETTO",
    vatPercent: "VAT",
    totalWithVAT: "RAZEM Z VAT",
    notesTitle: "Dodatkowe Uwagi",
    notesPlaceholder: "Wpisz uwagi...",
    skipperSignatureTitle: "Podpis Skippera",
    employeeSignatureTitle: "Podpis Pracownika",
    employeeCodeRequired: "\u26a0 Wymagany Kod Pracownika",
    employeeCodeNeeded: "\u26a0 Potrzebny kod pracownika!",
    signatureRequired: "Podpis wymagany",
    save: "Zapisz wersj\u0119 robocz\u0105",
    clear: "Wyczy\u015b\u0107",
    pdf: "PDF",
    back: "Wstecz",
    submit: "Wy\u015blij",
    ok: "OK - POTWIERDZAM",
    fieldRequired: "Pole wymagane",
    emailSent: "Emaile wys\u0142ane!",
    emailError: "B\u0142\u0105d wysy\u0142ki. Spr\u00f3buj ponownie.",
    checkInComplete: "Check-in zako\u0144czony!",
    pdfGenerated: "PDF wygenerowany!",
    dataSaved: "Dane zapisane!",
    footerAddress: "Leukosias 37, Alimos",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Tel: +30 6978196009"
  },
  he: {
    ...I18N.he,
    mainEquipment: "ציוד ראשי",
    hull: "גוף הסירה",
    mandatoryReading: "🔴 !קריאה חובה – לחצו כאן",
    skipperSignatureNote: "* חתימת הסקיפר למטה מכסה גם הרשאה זו",
    cancelBtn: "ביטול",
    pageTitle: "\u05e1\u05d9\u05d5\u05dd CHECK-IN/OUT",
    checkInMode: "\u2705 \u05de\u05e6\u05d1 \u05e6\u05f2\u05e7-\u05d0\u05d9\u05df",
    checkOutMode: "\ud83d\udce6 \u05de\u05e6\u05d1 \u05e6\u05f2\u05e7-\u05d0\u05d0\u05d5\u05d8",
    termsTitle: "\u05ea\u05e0\u05d0\u05d9\u05dd",
    privacyTitle: "הסכמה לפרטיות",
    privacyText: "אני מסכים/ה ל",
    privacyText2: " ומאשר/ת שימוש בפרטים האישיים שלי על ידי Tailwind Yachting.",
    returnTitle: "אישור מצב החזרה",
    returnText: "אני מאשר/ת שהיאכטה תוחזר עם כל הציוד הרשום. כל נזק או פריטים חסרים עלולים להיות מחויבים בהתאם למחירון.",
    termsText: "\u05d0\u05e0\u05d9 \u05de\u05e7\u05d1\u05dc \u05d0\u05ea \u05ea\u05e0\u05d0\u05d9 \u05d4\u05d4\u05e9\u05db\u05e8\u05d4. \u05d0\u05e0\u05d9 \u05de\u05d0\u05e9\u05e8 \u05e9\u05db\u05dc \u05d4\u05de\u05d9\u05d3\u05e2 \u05e0\u05db\u05d5\u05df.",
    warningTitle: "\u26a0\ufe0f \u05d0\u05d6\u05d4\u05e8\u05d4 \u05d7\u05e9\u05d5\u05d1\u05d4",
    warningTextPart1: "\u05e9\u05d9\u05de\u05d5 \u05dc\u05d1: \u05e0\u05d6\u05e7\u05d9\u05dd \u05e9\u05dc\u05d0 \u05d3\u05d5\u05d5\u05d7\u05d5 \u05d9\u05d9\u05d7\u05e9\u05d1\u05d5 \u05db\u05d0\u05d9\u05dc\u05d5 \u05d0\u05e8\u05e2\u05d5 \u05d1\u05ea\u05e7\u05d5\u05e4\u05ea \u05d4\u05d4\u05e9\u05db\u05e8\u05d4.",
    warningTextPart2: "\u05d4\u05e9\u05d5\u05db\u05e8 \u05d9\u05d4\u05d9\u05d4 \u05d0\u05d7\u05e8\u05d0\u05d9 \u05dc\u05db\u05dc \u05d4\u05e2\u05dc\u05d5\u05d9\u05d5\u05ea.",
    warningTextPart3: "\u05d1\u05d3\u05e7\u05d5 \u05d4\u05db\u05dc \u05dc\u05e4\u05e0\u05d9 \u05d4\u05d4\u05e4\u05dc\u05d2\u05d4.",
    warningTextPart4: "\u05ea\u05d5\u05d3\u05d4.",
    warningAccept: "\u2714 \u05e7\u05e8\u05d0\u05ea\u05d9 \u05d5\u05d4\u05d1\u05e0\u05ea\u05d9",
    paymentAuthTitle: "\u05d0\u05d9\u05e9\u05d5\u05e8 \u05ea\u05e9\u05dc\u05d5\u05dd",
    paymentAuthText: "\u05d4\u05dc\u05e7\u05d5\u05d7 \u05de\u05d0\u05e9\u05e8 \u05d7\u05d9\u05d5\u05d1 \u05db\u05e8\u05d8\u05d9\u05e1 \u05d1\u05d2\u05d9\u05df \u05e0\u05d6\u05e7\u05d9\u05dd.",
    paymentAuthAccept: "\u2714 \u05d0\u05e0\u05d9 \u05de\u05d0\u05e9\u05e8 \u05ea\u05e9\u05dc\u05d5\u05dd",
    cardReleaseTitle: "\u05e9\u05d7\u05e8\u05d5\u05e8 \u05d4\u05db\u05e8\u05d8\u05d9\u05e1",
    cardReleaseText: "\u05dc\u05d0 \u05e0\u05de\u05e6\u05d0\u05d5 \u05e0\u05d6\u05e7\u05d9\u05dd \u05d1\u05db\u05dc\u05d9 \u05d4\u05e9\u05d9\u05d8. \u05d4\u05dc\u05e7\u05d5\u05d7 \u05de\u05e1\u05db\u05d9\u05dd \u05dc\u05e9\u05d7\u05e8\u05d5\u05e8 \u05d4\u05e4\u05e8\u05d4-\u05d4\u05e8\u05e9\u05d0\u05d4 \u05e9\u05e0\u05e9\u05de\u05e8\u05d4 \u05d1\u05db\u05e8\u05d8\u05d9\u05e1\u05d5.",
    cardReleaseAccept: "\u2713 \u05dc\u05d0 \u05e0\u05de\u05e6\u05d0\u05d5 \u05e0\u05d6\u05e7\u05d9\u05dd \u2014 \u05e9\u05d7\u05e8\u05e8 \u05d0\u05ea \u05d4\u05db\u05e8\u05d8\u05d9\u05e1",
    completeInventory: "\u05de\u05dc\u05d0\u05d9 \u05de\u05dc\u05d0",
    damageInventory: "\u05d3\u05d5\u05d7 \u05e0\u05d6\u05e7\u05d9\u05dd",
    damageRate: "\u05e2\u05dc\u05d5\u05ea (\u05d0\u05dd \u05e0\u05d9\u05d6\u05d5\u05e7)",
    unitPrice: "\u05de\u05d7\u05d9\u05e8 \u05dc\u05d9\u05d7\u05d9\u05d3\u05d4",
    totalPrice: "\u05e1\u05d4\u05f4\u05db",
    netTotal: "\u05e1\u05d4\u05f4\u05db \u05e0\u05e7\u05d9",
    vatPercent: "\u05de\u05e2\u05f4\u05de",
    totalWithVAT: "\u05e1\u05d4\u05f4\u05db \u05db\u05d5\u05dc\u05dc \u05de\u05e2\u05f4\u05de",
    notesTitle: "\u05d4\u05e2\u05e8\u05d5\u05ea \u05e0\u05d5\u05e1\u05e4\u05d5\u05ea",
    notesPlaceholder: "\u05db\u05ea\u05d1\u05d5 \u05d4\u05e2\u05e8\u05d5\u05ea...",
    skipperSignatureTitle: "\u05d7\u05ea\u05d9\u05de\u05ea \u05d4\u05e1\u05e7\u05d9\u05e4\u05e8",
    employeeSignatureTitle: "\u05d7\u05ea\u05d9\u05de\u05ea \u05d4\u05e2\u05d5\u05d1\u05d3",
    employeeCodeRequired: "\u26a0 \u05e0\u05d3\u05e8\u05e9 \u05e7\u05d5\u05d3 \u05e2\u05d5\u05d1\u05d3",
    employeeCodeNeeded: "\u26a0 \u05e6\u05e8\u05d9\u05da \u05e7\u05d5\u05d3!",
    signatureRequired: "\u05d7\u05ea\u05d9\u05de\u05d4 \u05d7\u05d5\u05d1\u05d4",
    save: "\u05e9\u05de\u05d5\u05e8 \u05d8\u05d9\u05d5\u05d8\u05d4",
    clear: "\u05e0\u05e7\u05d4",
    pdf: "PDF",
    back: "\u05d7\u05d6\u05e8\u05d4",
    submit: "\u05e9\u05dc\u05d7",
    ok: "OK - \u05d0\u05e0\u05d9 \u05de\u05d0\u05e9\u05e8",
    fieldRequired: "\u05e9\u05d3\u05d4 \u05d7\u05d5\u05d1\u05d4",
    emailSent: "\u05d0\u05d9\u05de\u05d9\u05d9\u05dc\u05d9\u05dd \u05e0\u05e9\u05dc\u05d7\u05d5!",
    emailError: "\u05e9\u05d2\u05d9\u05d0\u05d4 \u05d1\u05e9\u05dc\u05d9\u05d7\u05d4. \u05e0\u05e1\u05d5 \u05e9\u05d5\u05d1.",
    checkInComplete: "\u05e6\u05f2\u05e7-\u05d0\u05d9\u05df \u05d4\u05d5\u05e9\u05dc\u05dd!",
    pdfGenerated: "PDF \u05e0\u05d5\u05e6\u05e8!",
    dataSaved: "\u05e0\u05ea\u05d5\u05e0\u05d9\u05dd \u05e0\u05e9\u05de\u05e8\u05d5!",
    footerAddress: "Leukosias 37, Alimos",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Tel: +30 6978196009"
  },
  es: {
    ...I18N.es,
    mainEquipment: "Equipamiento Principal",
    hull: "Casco",
    mandatoryReading: "🔴 LECTURA OBLIGATORIA – ¡HAGA CLIC AQUÍ!",
    skipperSignatureNote: "* La firma del skipper a continuación cubre también esta autorización",
    cancelBtn: "Cancelar",
    pageTitle: "FINALIZAR CHECK-IN/OUT",
    checkInMode: "\u2705 Modo Check-in",
    checkOutMode: "\ud83d\udce6 Modo Check-out",
    termsTitle: "T\u00e9rminos y Condiciones",
    privacyTitle: "Consentimiento de Privacidad",
    privacyText: "Acepto la ",
    privacyText2: " y consiento el uso de mis datos personales por parte de Tailwind Yachting.",
    returnTitle: "Reconocimiento del Estado de Devolución",
    returnText: "Confirmo que el yate será devuelto con todo el equipamiento listado. Cualquier daño o artículo faltante podrá ser cobrado según la lista de precios.",
    termsText: "Acepto los t\u00e9rminos del alquiler. Confirmo que toda la informaci\u00f3n es correcta y he inspeccionado la embarcaci\u00f3n.",
    warningTitle: "\u26a0\ufe0f Aviso Importante",
    warningTextPart1: "ATENCI\u00d3N: Los da\u00f1os no registrados se considerar\u00e1n ocurridos durante el per\u00edodo de alquiler.",
    warningTextPart2: "El arrendatario ser\u00e1 responsable de todos los costes.",
    warningTextPart3: "Inspeccione todo cuidadosamente antes de zarpar.",
    warningTextPart4: "Gracias de antemano.",
    warningAccept: "\u2714 He le\u00eddo y entendido",
    paymentAuthTitle: "Autorizaci\u00f3n de Pago",
    paymentAuthText: "El cliente autoriza el cargo en tarjeta por da\u00f1os detectados.",
    paymentAuthAccept: "\u2714 Autorizo el pago",
    cardReleaseTitle: "Liberaci\u00f3n de la Tarjeta",
    cardReleaseText: "No se encontraron da\u00f1os en la embarcaci\u00f3n. El cliente consiente la liberaci\u00f3n de la preautorizaci\u00f3n retenida en su tarjeta.",
    cardReleaseAccept: "\u2714 No se encontraron da\u00f1os \u2014 liberar la tarjeta",
    completeInventory: "INVENTARIO COMPLETO",
    damageInventory: "INFORME DE DA\u00d1OS",
    damageRate: "Coste (si da\u00f1ado)",
    unitPrice: "Precio Unitario",
    totalPrice: "Total",
    netTotal: "TOTAL NETO",
    vatPercent: "IVA",
    totalWithVAT: "TOTAL CON IVA",
    notesTitle: "Observaciones Adicionales",
    notesPlaceholder: "Escriba observaciones...",
    skipperSignatureTitle: "Firma del Patr\u00f3n",
    employeeSignatureTitle: "Firma del Empleado",
    employeeCodeRequired: "\u26a0 C\u00f3digo de Empleado Requerido",
    employeeCodeNeeded: "\u26a0 Se necesita c\u00f3digo!",
    signatureRequired: "Firma obligatoria",
    save: "Guardar borrador",
    clear: "Limpiar",
    pdf: "PDF",
    back: "Atr\u00e1s",
    submit: "Enviar",
    ok: "OK - CONFIRMO",
    fieldRequired: "Campo obligatorio",
    emailSent: "\u00a1Emails enviados con \u00e9xito!",
    emailError: "Error de env\u00edo. Reintente.",
    checkInComplete: "\u00a1Check-in completado!",
    pdfGenerated: "\u00a1PDF generado!",
    dataSaved: "\u00a1Datos guardados!",
    footerAddress: "Leukosias 37, Alimos",
    footerWebsite: "www.tailwindyachting.com",
    footerPhone: "Tel: +30 6978196009"
  },

};

// =================================================================
// HELPER FUNCTIONS
// =================================================================

function getItemLabel(key, lang = 'en') {
  return ITEM_LABELS[key]?.[lang] || ITEM_LABELS[key]?.['en'] || key;
}

// Transform items array into CompleteInventory format
function transformItemsToInventory(items: any[], page: string, sectionParam: any, lang: string = 'en'): any[] {
    const section = typeof sectionParam === 'string' ? sectionParam : (sectionParam.en || sectionParam[lang] || sectionParam);
    const sectionEn = typeof sectionParam === 'string' ? sectionParam : (sectionParam.en || section);
  if (!items || !Array.isArray(items)) return [];

  return items.map(item => {
    // Debug: Log items with media
    if (item.media && item.media.length > 0) {
      console.log(`📷 [${page}/${section}] Item "${item.key}" has ${item.media.length} photos`);
    }
    return {
      page,
      section,
      name: getItemLabel(item.key, lang) || item.key,
            nameEn: getItemLabel(item.key, 'en') || item.key,
      qty: item.qty || 1,
      inOk: item.inOk || false,
      out: item.out || null,
      price: item.price || '0',
      media: item.media || [] // Include photos/media
    };
  });
}

// Transform Page 2 data to inventory items
function transformPage2Data(data: any, lang: string = 'en'): any[] {
  if (!data) return [];
  const results: any[] = [];

  // Main equipment items
  if (data.items && Array.isArray(data.items)) {
    results.push(...transformItemsToInventory(data.items, 'Equipment Inspection', {en:'Main Equipment',el:'Κύριος Εξοπλισμός',it:'Equipaggiamento Principale',de:'Hauptausrüstung',ru:'Основное оборудование',fr:'Équipement Principal',ro:'Echipament Principal',pl:'Wyposażenie Główne',he:'ציוד ראשי',es:'Equipamiento Principal'}, lang));
  }

  // Hull items
  if (data.hullItems && Array.isArray(data.hullItems)) {
    results.push(...transformItemsToInventory(data.hullItems, 'Equipment Inspection', {en:'Hull',el:'Κύτος',it:'Scafo',de:'Rumpf',ru:'Корпус',fr:'Coque',ro:'Coca',pl:'Kadłub',he:'גוף הסירה',es:'Casco'}, lang));
  }

  // Dinghy items
  if (data.dinghyItems && Array.isArray(data.dinghyItems)) {
    results.push(...transformItemsToInventory(data.dinghyItems, 'Equipment Inspection', {en:'Dinghy',el:'Λέμβος',it:'Tender',de:'Beiboot',ru:'Тузик',fr:'Annexe',ro:'Barcă',pl:'Ponton',he:'סירת גומי',es:'Bote'}, lang));
  }

  return results;
}

// Transform Page 3 data to inventory items
function transformPage3Data(data: any, lang: string = 'en'): any[] {
  if (!data) return [];
  const results: any[] = [];

  // Safety items
  if (data.safetyItems && Array.isArray(data.safetyItems)) {
    results.push(...transformItemsToInventory(data.safetyItems, 'Page 3', {en:'Safety Equipment',el:'Εξοπλισμός Ασφαλείας',it:'Equipaggiamento di Sicurezza',de:'Sicherheitsausrüstung',ru:'Оборудование безопасности',fr:'Équipement de Sécurité',ro:'Echipament de Siguranță',pl:'Wyposażenie Bezpieczeństwa',he:'ציוד בטיחות',es:'Equipo de Seguridad'}, lang));
  }

  // Cabin items
  if (data.cabinItems && Array.isArray(data.cabinItems)) {
    results.push(...transformItemsToInventory(data.cabinItems, 'Page 3', {en:'Cabin',el:'Καμπίνα',it:'Cabina',de:'Kabine',ru:'Каюта',fr:'Cabine',ro:'Cabină',pl:'Kabina',he:'תא',es:'Camarote'}, lang));
  }

  // Optional items
  if (data.optionalItems && Array.isArray(data.optionalItems)) {
    results.push(...transformItemsToInventory(data.optionalItems, 'Page 3', {en:'Optional',el:'Προαιρετικά',it:'Opzionali',de:'Optional',ru:'Дополнительно',fr:'Optionnel',ro:'Opțional',pl:'Opcjonalne',he:'אופציונלי',es:'Opcional'}, lang));
  }

  return results;
}

// Transform Page 4 data to inventory items
function transformPage4Data(data: any, lang: string = 'en'): any[] {
  if (!data) return [];
  const results: any[] = [];

  // Navigation items
  if (data.navItems && Array.isArray(data.navItems)) {
    results.push(...transformItemsToInventory(data.navItems, 'Detailed Inspection', {en:'Navigation',el:'Πλοήγηση',it:'Navigazione',de:'Navigation',ru:'Навигация',fr:'Navigation',ro:'Navigație',pl:'Nawigacja',he:'ניווט',es:'Navegación'}, lang));
  }

  // Safety items
  if (data.safetyItems && Array.isArray(data.safetyItems)) {
    results.push(...transformItemsToInventory(data.safetyItems, 'Detailed Inspection', {en:'Safety',el:'Ασφάλεια',it:'Sicurezza',de:'Sicherheit',ru:'Безопасность',fr:'Sécurité',ro:'Siguranță',pl:'Bezpieczeństwo',he:'בטיחות',es:'Seguridad'}, lang));
  }

  // Generator items
  if (data.genItems && Array.isArray(data.genItems)) {
    results.push(...transformItemsToInventory(data.genItems, 'Detailed Inspection', {en:'Generator',el:'Γεννήτρια',it:'Generatore',de:'Generator',ru:'Генератор',fr:'Générateur',ro:'Generator',pl:'Generator',he:'גנרטור',es:'Generador'}, lang));
  }

  // Deck items
  if (data.deckItems && Array.isArray(data.deckItems)) {
    results.push(...transformItemsToInventory(data.deckItems, 'Detailed Inspection', {en:'Deck',el:'Κατάστρωμα',it:'Ponte',de:'Deck',ru:'Палуба',fr:'Pont',ro:'Punte',pl:'Pokład',he:'סיפון',es:'Cubierta'}, lang));
  }

  // Front deck items
  if (data.fdeckItems && Array.isArray(data.fdeckItems)) {
    results.push(...transformItemsToInventory(data.fdeckItems, 'Detailed Inspection', {en:'Front Deck',el:'Πλώρη',it:'Ponte Anteriore',de:'Vordeck',ru:'Носовая палуба',fr:'Pont Avant',ro:'Punte Față',pl:'Pokład Dziobowy',he:'סיפון קדמי',es:'Cubierta Delantera'}, lang));
  }

  // Dinghy items
  if (data.dinghyItems && Array.isArray(data.dinghyItems)) {
    results.push(...transformItemsToInventory(data.dinghyItems, 'Detailed Inspection', {en:'Dinghy',el:'Λέμβος',it:'Tender',de:'Beiboot',ru:'Тузик',fr:'Annexe',ro:'Barcă',pl:'Ponton',he:'סירת גומי',es:'Bote'}, lang));
  }

  // Fenders items
  if (data.fendersItems && Array.isArray(data.fendersItems)) {
    results.push(...transformItemsToInventory(data.fendersItems, 'Detailed Inspection', {en:'Fenders',el:'Μπαλόνια',it:'Parabordi',de:'Fender',ru:'Кранцы',fr:'Pare-battages',ro:'Apărători',pl:'Odbijacze',he:'פנדרים',es:'Defensas'}, lang));
  }

  // Boathook items
  if (data.boathookItems && Array.isArray(data.boathookItems)) {
    results.push(...transformItemsToInventory(data.boathookItems, 'Detailed Inspection', {en:'Boathook',el:'Γάντζος',it:'Mezzo Marinaio',de:'Bootshaken',ru:'Отпорный крюк',fr:'Gaffe',ro:'Cange',pl:'Bosak',he:'אנקול',es:'Bichero'}, lang));
  }

  // Main items (if present)
  if (data.items && Array.isArray(data.items)) {
    results.push(...transformItemsToInventory(data.items, 'Detailed Inspection', {en:'General',el:'Γενικά',it:'Generale',de:'Allgemein',ru:'Общее',fr:'Général',ro:'General',pl:'Ogólne',he:'כללי',es:'General'}, lang));
  }

  return results;
}

// Load all inventory items from Pages 2, 3, 4
async function loadAllInventoryItems(bookingNumber: string, mode: 'in' | 'out', lang: string = 'en'): Promise<any[]> {
  const allItems: any[] = [];


  // 🔥 Load media from localStorage (photos stored separately from API)
  // For check-out mode, load media from BOTH check-in AND check-out (photos taken during check-in should still appear)
  const page2MediaCurrent = getPageMedia(bookingNumber, mode, 'page2');
  const page3MediaCurrent = getPageMedia(bookingNumber, mode, 'page3');
  const page4MediaCurrent = getPageMedia(bookingNumber, mode, 'page4');

  // Start with current mode's media
  let page2Media: Record<string, any[]> = { ...page2MediaCurrent };
  let page3Media: Record<string, any[]> = { ...page3MediaCurrent };
  let page4Media: Record<string, any[]> = { ...page4MediaCurrent };

  // If in check-out mode, also load check-in photos
  if (mode === 'out') {
    const page2MediaIn = getPageMedia(bookingNumber, 'in', 'page2');
    const page3MediaIn = getPageMedia(bookingNumber, 'in', 'page3');
    const page4MediaIn = getPageMedia(bookingNumber, 'in', 'page4');

    // Merge check-in photos with check-out photos (combine both)
    Object.keys(page2MediaIn).forEach(key => {
      if (!page2Media[key]) page2Media[key] = page2MediaIn[key];
      else page2Media[key] = [...page2MediaIn[key], ...page2Media[key]];
    });
    Object.keys(page3MediaIn).forEach(key => {
      if (!page3Media[key]) page3Media[key] = page3MediaIn[key];
      else page3Media[key] = [...page3MediaIn[key], ...page3Media[key]];
    });
    Object.keys(page4MediaIn).forEach(key => {
      if (!page4Media[key]) page4Media[key] = page4MediaIn[key];
      else page4Media[key] = [...page4MediaIn[key], ...page4Media[key]];
    });

    console.log('📷 Check-in media also loaded for check-out mode');
  }

  console.log('📷 Media loaded from localStorage:', {
    page2: Object.keys(page2Media).length + ' items',
    page3: Object.keys(page3Media).length + ' items',
    page4: Object.keys(page4Media).length + ' items'
  });

  // ===== DYNAMIC LOADER (master_items + master_sections, source of truth = page_group) =====
  try {
    const [masterItems, masterSections, p2Data, p3Data, p4Data] = await Promise.all([
      getMasterItems().catch(() => []),
      getMasterSections().catch(() => []),
      getPage2DataHybrid(bookingNumber, mode).catch(() => null),
      getPage3DataHybrid(bookingNumber, mode).catch(() => null),
      getPage4DataHybrid(bookingNumber, mode).catch(() => null),
    ]);

    const itemLookup: any = {};
    (masterItems || []).forEach((mi: any) => { itemLookup[mi.item_key] = mi; });

    const sectionLookup: any = {};
    (masterSections || []).forEach((ms: any) => { sectionLookup[ms.section_key] = ms; });

    const collectItems = (pageData: any): any[] => {
      if (!pageData) return [];
      const collected: any[] = [];
      const propNames = ['items','hullItems','dinghyItems','navItems','safetyItems','genItems','deckItems','fdeckItems','fendersItems','boathookItems','kitchenItems','cabinItems','optionalItems','technicalCheckItems'];
      // Stage 5γ (v6.93): include dynamic master_sections.section_key containers (e.g. page2 'cabin')
      const dynKeys = (Array.isArray(masterSections) ? masterSections : [])
        .map((s: any) => s && s.section_key)
        .filter((k: any) => k && !propNames.includes(k));
      const allKeys = [...propNames, ...dynKeys];
      for (const prop of allKeys) {
        if (Array.isArray(pageData[prop])) collected.push(...pageData[prop]);
      }
      return collected;
    };

    const allSaved = [
      ...collectItems(p2Data).map((it: any) => ({ ...it, _mediaStore: page2Media })),
      ...collectItems(p3Data).map((it: any) => ({ ...it, _mediaStore: page3Media })),
      ...collectItems(p4Data).map((it: any) => ({ ...it, _mediaStore: page4Media })),
    ];

    const dynamicResults: any[] = [];
    for (const savedItem of allSaved) {
      if (!savedItem || !savedItem.key) continue;
      const master = itemLookup[savedItem.key];
      if (!master) continue;
      const section = sectionLookup[master.section];
      const pageLabel = master.page_group ? master.page_group.replace('page', 'Page ') : 'Unknown';
      const langKey = 'title_' + lang;
      const labelKey = 'label_' + lang;
      const sectionTitle = section ? (section[langKey] || section.title_en || master.section) : master.section;
      const mediaStore = savedItem._mediaStore || {};
      const itemMedia = mediaStore[savedItem.key] || savedItem.media || [];
      dynamicResults.push({
        // Stage 5γ (v6.93): temp sort keys — stripped after sort, μηδέν επίδραση downstream
        _po: ({ page2: 0, page3: 1, page4: 2 } as any)[master.page_group] ?? 99,
        _so: (section && Number.isFinite(section.sort_order)) ? section.sort_order : 999,
        _si: (section && Number.isFinite(section.id)) ? section.id : Number.MAX_SAFE_INTEGER,
        _io: Number.isFinite(master.sort_order) ? master.sort_order : 999,
        _ii: Number.isFinite(master.id) ? master.id : Number.MAX_SAFE_INTEGER,
        key: savedItem.key, // needed to key per-charter price overrides (check-out) to shapeItem
        page: pageLabel,
        section: sectionTitle,
        name: master[labelKey] || master.label_en || savedItem.key,
        nameEn: master.label_en || savedItem.key,
        qty: savedItem.qty || 1,
        inOk: savedItem.inOk || false,
        out: savedItem.out || null,
        price: savedItem.price || '0',
        media: itemMedia
      });
    }

    // Stage 5γ (v6.93): group by page → section → item (stable, contiguous headers)
    dynamicResults.sort((a: any, b: any) =>
      (a._po - b._po) ||
      (a._so - b._so) ||
      (a._si - b._si) ||
      (a._io - b._io) ||
      (a._ii - b._ii) ||
      0
    );
    dynamicResults.forEach((r: any) => {
      delete r._po; delete r._so; delete r._si; delete r._io; delete r._ii;
    });

    console.log(`Dynamic loader: ${dynamicResults.length} items from master lookup`);
    allItems.push(...dynamicResults);

    if (dynamicResults.length > 0) {
      console.log(`Total inventory items loaded (dynamic): ${allItems.length}`);
      return allItems;
    }
    console.warn('Dynamic loader produced 0 items, falling back to legacy');
  } catch (e) {
    console.warn('Dynamic loader failed, falling back to legacy:', e);
  }
  // ===== END DYNAMIC LOADER =====

  try {
    // Load Page 2 data
    const page2Data = await getPage2DataHybrid(bookingNumber, mode);
    if (page2Data) {
      console.log('📦 Page 2 data loaded:', page2Data);
      // 🔥 Merge media from localStorage into items
      if (page2Data.items) page2Data.items = mergeMediaIntoItems(page2Data.items, page2Media);
      if (page2Data.hullItems) page2Data.hullItems = mergeMediaIntoItems(page2Data.hullItems, page2Media);
      if (page2Data.dinghyItems) page2Data.dinghyItems = mergeMediaIntoItems(page2Data.dinghyItems, page2Media);
      // Debug: Check if media exists after merge
      if (page2Data.items) {
        const itemsWithMedia = page2Data.items.filter((i: any) => i.media && i.media.length > 0);
        console.log(`📷 Page 2 items with media after merge: ${itemsWithMedia.length}`);
        itemsWithMedia.forEach((i: any) => {
          console.log(`  - Item "${i.key}": ${i.media.length} photos, first photo structure:`, {
            type: typeof i.media[0],
            hasUrl: !!i.media[0]?.url,
            hasData: !!i.media[0]?.data,
            urlPreview: i.media[0]?.url?.substring(0, 80) || 'N/A'
          });
        });
      }
      allItems.push(...transformPage2Data(page2Data, lang));
    }
  } catch (error) {
    console.warn('⚠️ Failed to load Page 2 data:', error);
  }

  try {
    // Load Page 3 data
    const page3Data = await getPage3DataHybrid(bookingNumber, mode);
    if (page3Data) {
      console.log('📦 Page 3 data loaded:', page3Data);
      // 🔥 Merge media from localStorage into items
      if (page3Data.safetyItems) page3Data.safetyItems = mergeMediaIntoItems(page3Data.safetyItems, page3Media);
      if (page3Data.cabinItems) page3Data.cabinItems = mergeMediaIntoItems(page3Data.cabinItems, page3Media);
      if (page3Data.optionalItems) page3Data.optionalItems = mergeMediaIntoItems(page3Data.optionalItems, page3Media);
      allItems.push(...transformPage3Data(page3Data, lang));
    }
  } catch (error) {
    console.warn('⚠️ Failed to load Page 3 data:', error);
  }

  try {
    // Load Page 4 data
    const page4Data = await getPage4DataHybrid(bookingNumber, mode);
    if (page4Data) {
      console.log('📦 Page 4 data loaded:', page4Data);
      // 🔥 Merge media from localStorage into items
      if (page4Data.items) page4Data.items = mergeMediaIntoItems(page4Data.items, page4Media);
      if (page4Data.navItems) page4Data.navItems = mergeMediaIntoItems(page4Data.navItems, page4Media);
      if (page4Data.safetyItems) page4Data.safetyItems = mergeMediaIntoItems(page4Data.safetyItems, page4Media);
      if (page4Data.genItems) page4Data.genItems = mergeMediaIntoItems(page4Data.genItems, page4Media);
      if (page4Data.deckItems) page4Data.deckItems = mergeMediaIntoItems(page4Data.deckItems, page4Media);
      if (page4Data.fdeckItems) page4Data.fdeckItems = mergeMediaIntoItems(page4Data.fdeckItems, page4Media);
      if (page4Data.dinghyItems) page4Data.dinghyItems = mergeMediaIntoItems(page4Data.dinghyItems, page4Media);
      if (page4Data.fendersItems) page4Data.fendersItems = mergeMediaIntoItems(page4Data.fendersItems, page4Media);
      if (page4Data.boathookItems) page4Data.boathookItems = mergeMediaIntoItems(page4Data.boathookItems, page4Media);
      allItems.push(...transformPage4Data(page4Data, lang));
    }
  } catch (error) {
    console.warn('⚠️ Failed to load Page 4 data:', error);
  }

  console.log(`📋 Total inventory items loaded: ${allItems.length}`);
  const itemsWithPhotos = allItems.filter(i => i.media && i.media.length > 0);
  console.log(`📷 Items with photos: ${itemsWithPhotos.length}`, itemsWithPhotos.map(i => ({ name: i.name, photos: i.media.length })));
  return allItems;
}

function getDamagePhotos(mode) {
  // DEPRECATED: This function needs refactoring to use API data
  console.warn('⚠️ getDamagePhotos: localStorage for bookings removed - photos should be loaded via API');
  if (mode !== 'out') return {};
  return {};
}

function getAllPhotos() {
  // DEPRECATED: This function needs refactoring to use API data
  console.warn('⚠️ getAllPhotos: localStorage for bookings removed - photos should be loaded via API');
  return {};
}

async function sendEmailWithPDF(bookingData, pdfBlob, mode, lang, additionalData?: any) {
  const t = Page5_I18N[lang] || Page5_I18N.en;
  try {
    const _emailList = [bookingData.skipperEmail, bookingData.chartererEmail]
      .map((e: any) => (e || '').toString().trim())
      .filter((e: string) => e.length > 0);
    const _seen = new Set<string>();
    const _dedup = _emailList.filter((e: string) => {
      const k = e.toLowerCase();
      if (_seen.has(k)) return false;
      _seen.add(k);
      return true;
    });
    const customerEmail = _dedup.join(',');

    // Use the centralized email service with additional data (inventory, agreements, notes)
    if (mode === 'in') {
      return await sendCheckInEmail(customerEmail, bookingData, pdfBlob, additionalData, lang);
    } else {
      return await sendCheckOutEmail(customerEmail, bookingData, pdfBlob, additionalData, lang);
    }
  } catch (error) {
    console.error('Email sending error:', error);
    return { success: false, error };
  }
}

// =================================================================
// END OF PART 1
// Continue with PART 2 (UI Components)
// =================================================================
// =================================================================
// PAGE 5 - FINAL CORRECT VERSION - PART 2 OF 4
// =================================================================
// UI COMPONENTS - ALL CORRECT
// =================================================================
// PASTE THIS AFTER PART 1
// =================================================================

// =================================================================
// SIGNATURE BOX COMPONENT
// =================================================================
const SignatureBox = ({ 
  brand, 
  lang, 
  title, 
  onSignChange, 
  onImageChange, 
  initialImage, 
  currentBookingNumber, 
  mode, 
  pageNumber,
  disabled = false,
  highlightError = false
}) => {
  const canvasRef = useRef(null);
  useSignatureTouch(canvasRef);
  const [signed, setSigned] = useState(!!initialImage);

  useEffect(() => {
    setSigned(!!initialImage);
  }, [initialImage]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (initialImage) {
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setSigned(true);
        if (onSignChange) onSignChange(true);
      };
      img.src = initialImage;
    }

    const ctx = canvas.getContext('2d');
    let drawing = false;
    let lastX = 0;
    let lastY = 0;

    const getCoordinates = (e) => {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches ? e.touches[0] : e;
      return {
        x: (touch.clientX - rect.left) * (canvas.width / rect.width),
        y: (touch.clientY - rect.top) * (canvas.height / rect.height)
      };
    };

    const startDrawing = (e) => {
      if (disabled) return;
      drawing = true;
      const coords = getCoordinates(e);
      lastX = coords.x;
      lastY = coords.y;
    };

    const draw = (e) => {
      if (!drawing || disabled) return;
      e.preventDefault();
      
      const coords = getCoordinates(e);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
      
      lastX = coords.x;
      lastY = coords.y;
      
      if (!signed) {
        setSigned(true);
        if (onSignChange) onSignChange(true);
      }
    };

    const stopDrawing = () => {
      if (!drawing) return;
      drawing = false;
      
      if (onImageChange) {
        const imageData = canvas.toDataURL('image/png');
        onImageChange(imageData);
      }
    };

    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseleave', stopDrawing);
    
    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDrawing);

    return () => {
      canvas.removeEventListener('mousedown', startDrawing);
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
      canvas.removeEventListener('mouseleave', stopDrawing);
      canvas.removeEventListener('touchstart', startDrawing);
      canvas.removeEventListener('touchmove', draw);
      canvas.removeEventListener('touchend', stopDrawing);
    };
  }, [disabled, onSignChange, onImageChange, initialImage, signed]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
    if (onSignChange) onSignChange(false);
    if (onImageChange) onImageChange(null);
  };

  const t = Page5_I18N[lang] || Page5_I18N.en;

  return (
    <div 
      className="border-2 rounded-xl p-4 mt-4 transition-all duration-500" 
      style={{ 
        borderColor: highlightError ? brand.errorBorder : (signed ? brand.successBorder : brand.blue), 
        background: highlightError ? brand.errorBg : (signed ? brand.successBg : "#ffffff"),
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto'
      }}
    >
      <label className="block font-semibold mb-2" style={{ color: brand.black }}>
        {title} *
      </label>
      <div style={{
        border: "2px solid",
        borderColor: signed ? brand.successBorder : brand.black,
        background: signed ? brand.successBg : "#ffffff",
        borderRadius: 12,
        padding: 8
      }}>
        <canvas 
          ref={canvasRef} 
          width={600} 
          height={180} 
          className="w-full max-w-full h-[180px] md:h-[200px] cursor-crosshair"
          style={{ background: signed ? brand.successBg : '#ffffff', touchAction: 'none', transition: 'background 0.3s ease' }}
        />
      </div>
      <div className="flex justify-between items-center mt-2">
        {!signed && (
          <div className="text-sm" style={{ color: brand.errorBorder }}>
            {t.signatureRequired}
          </div>
        )}
        <button 
          type="button" 
          onClick={clearSignature} 
          className="px-3 py-1 rounded border ml-auto" 
          style={{ borderColor: brand.black }}
          disabled={disabled}
        >
          {t.clear}
        </button>
      </div>
    </div>
  );
};

// =================================================================
// AGREEMENT BOX
// =================================================================
function AgreementBox({ title, text, link, text2, accepted, setAccepted, t, required = false, id, highlightError }) {
  return (
    <div id={id} className="mb-4">
      <div 
        className="rounded-xl p-4 transition-all duration-500" 
        style={{ 
          border: `2px solid ${highlightError ? brand.errorBorder : (accepted ? brand.successBorder : brand.blue)}`, 
          background: highlightError ? brand.errorBg : (accepted ? brand.successBg : "transparent") 
        }}
      >
        <div className="font-semibold" style={{ color: brand.black }}>
          {title} {required && "*"}
        </div>
        <div className="mt-3 flex items-start gap-3">
          <input 
            type="checkbox" 
            checked={accepted} 
            onChange={() => {
              if (!accepted) {
                setAccepted(true);
              }
            }}
            className="mt-1 cursor-pointer" 
          />
          <p className="text-[15px]" style={{ color: brand.black }}>
            {text}
            {link && (
              <button 
                type="button" 
                className="underline font-bold" 
                style={{ color: "red" }} 
                onClick={() => alert("Opening " + link + "...")}
              >
                {link}
              </button>
            )}
            {text2 && text2}
          </p>
        </div>
        <div className="flex justify-end mt-3">
          <button 
            type="button" 
            onClick={() => setAccepted(true)} 
            disabled={accepted} 
            className="px-3 py-1 rounded text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed" 
            style={{ background: brand.blue }}
          >
            {t.ok}
          </button>
        </div>
      </div>
      {required && !accepted && (
        <div className="mt-1 inline-block text-xs px-2 py-1 rounded border" style={{ color: "#ef4444", borderColor: "#ef4444" }}>
          {t.fieldRequired}
        </div>
      )}
    </div>
  );
}

// =================================================================
// DAMAGE INVENTORY (CHECK-OUT) - WITH PAGE COLUMN
// =================================================================
function DamageInventory({ items, t, lang, overrides, onPriceChange }) {
  const [zoomedPhoto, setZoomedPhoto] = React.useState(null);

  if (items.length === 0) return null;

  // Get VAT rate from settings
  let vatRate = 24;
  try {
    vatRate = getVATRate() || 24;
  } catch (e) {
    vatRate = 24; // Default to 24%
  }

  // Effective τιμή = override (αν πληκτρολογήθηκε) αλλιώς η αποθηκευμένη. Live recalc σε κάθε render.
  const effPrice = (item: any) => {
    const ov = overrides && overrides[item.key];
    const raw = ov != null && ov !== '' ? ov : (item.price ?? '');
    return parseFloat(String(raw).replace(',', '.')) || 0; // δέχεται κόμμα Ή τελεία (150,50 = 150.50)
  };
  let totalAmount = 0;
  items.forEach(item => {
    totalAmount += (item.qty || 1) * effPrice(item);
  });

  // Calculate VAT
  const netTotal = totalAmount;
  const vatAmount = netTotal * (vatRate / 100);
  const totalWithVat = netTotal + vatAmount;

  // Collect all photos with item info
  const allDamagePhotos = [];
  items.forEach((item, idx) => {
    const photos = item.media || [];
    // 🔥 Debug: Log photo structure
    if (photos.length > 0) {
      console.log(`📷 DamageInventory: Item "${item.name}" has ${photos.length} photos:`,
        photos.map((p, i) => ({
          index: i,
          type: typeof p,
          hasUrl: !!p?.url,
          hasData: !!p?.data,
          urlPreview: p?.url?.substring(0, 50) || p?.data?.substring(0, 50) || 'N/A'
        }))
      );
    }
    photos.forEach((photo, photoIdx) => {
      allDamagePhotos.push({
        itemName: item.name,
        itemPage: item.page,
        photo,
        idx: `${idx}-${photoIdx}`
      });
    });
  });

  return (
    <div className="mb-6">
      <div className="rounded-xl border-2 p-4" style={{ borderColor: brand.blue, background: "#fff5f5" }}>
        <h3 className="font-bold text-xl mb-4 text-center" style={{ color: '#dc2626' }}>
          {t.damageInventory}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ color: brand.black }}>
            <thead>
              <tr className="border-b-2" style={{ borderColor: '#dc2626', backgroundColor: '#fee2e2' }}>
                <th className="text-left p-2 font-bold">Page</th>
                <th className="text-left p-2 font-bold">Item</th>
                <th className="text-center p-2 font-bold">Qty</th>
                <th className="text-center p-2 font-bold">📷</th>
                <th className="text-right p-2 font-bold">{t.unitPrice}</th>
                <th className="text-right p-2 font-bold">{t.totalPrice}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const qty = item.qty || 1;
                const unitPrice = effPrice(item);
                const total = qty * unitPrice;
                const photoCount = (item.media || []).length;

                return (
                  <tr key={idx} className="border-t" style={{ borderColor: '#fee2e2' }}>
                    <td className="p-2 text-xs text-gray-600">{item.page}</td>
                    <td className="p-2 font-semibold" style={{ color: brand.black }}>{item.name}</td>
                    <td className="p-2 text-center font-bold">{qty}</td>
                    <td className="p-2 text-center">
                      {photoCount > 0 ? (
                        <span className="inline-flex items-center justify-center bg-red-100 text-red-600 text-xs font-bold rounded-full w-6 h-6">
                          {photoCount}
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      <input
                        type="text" inputMode="decimal"
                        value={overrides?.[item.key] ?? (item.price ?? '')}
                        onChange={(e) => onPriceChange(item.key, e.target.value.replace(/[^0-9.,]/g, ''))}
                        placeholder="0,00"
                        className="w-24 px-2 py-1 text-right border-2 rounded font-semibold focus:outline-none"
                        style={{ borderColor: '#dc2626', color: brand.black }}
                      />
                    </td>
                    <td className="p-2 text-right font-bold" style={{ color: '#dc2626' }}>€{total.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Damage Photos Gallery */}
        {allDamagePhotos.length > 0 && (
          <div className="mt-6 pt-4 border-t-2" style={{ borderColor: '#dc2626' }}>
            <h4 className="font-bold text-base mb-3" style={{ color: '#dc2626' }}>
              📷 {t.damagePhotosTitle || 'Damage Photos'} ({allDamagePhotos.length})
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {allDamagePhotos.map(({ itemName, itemPage, photo, idx }) => {
                const photoUrl = photo?.url || photo?.data || (typeof photo === 'string' ? photo : null);
                if (!photoUrl) return null;

                return (
                  <div
                    key={idx}
                    className="relative rounded-lg overflow-hidden border-2 border-red-200 cursor-pointer hover:border-red-400 transition-colors"
                    onClick={() => setZoomedPhoto(photoUrl)}
                  >
                    <img
                      src={photoUrl}
                      alt={`${itemName} damage`}
                      className="w-full h-24 object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1">
                      <p className="text-white text-xs truncate font-semibold">{itemName}</p>
                      <p className="text-gray-300 text-xs">{itemPage}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t-2" style={{ borderColor: '#dc2626' }}>
          {/* NET TOTAL */}
          <div className="flex justify-between items-center py-2 px-3 mb-1" style={{ backgroundColor: '#f3f4f6' }}>
            <span className="text-base font-bold" style={{ color: brand.black }}>{t.netTotal}:</span>
            <span className="text-lg font-bold" style={{ color: brand.black }}>€{netTotal.toFixed(2)}</span>
          </div>
          {/* VAT */}
          <div className="flex justify-between items-center py-2 px-3 mb-1" style={{ backgroundColor: '#f3f4f6' }}>
            <span className="text-base font-bold" style={{ color: brand.black }}>{t.vatPercent} {vatRate}%:</span>
            <span className="text-lg font-bold" style={{ color: brand.black }}>€{vatAmount.toFixed(2)}</span>
          </div>
          {/* TOTAL WITH VAT */}
          <div className="flex justify-between items-center py-3 px-3 rounded-lg border-2" style={{ backgroundColor: '#fee2e2', borderColor: '#dc2626' }}>
            <span className="text-xl font-bold" style={{ color: '#dc2626' }}>{t.totalWithVAT}:</span>
            <span className="text-2xl font-bold" style={{ color: '#dc2626' }}>€{totalWithVat.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Photo Zoom Modal */}
      {zoomedPhoto && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setZoomedPhoto(null)}
        >
          <img
            src={zoomedPhoto}
            alt="Zoomed damage photo"
            className="max-w-[90%] max-h-[90%] rounded-lg shadow-2xl"
          />
          <button
            className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300"
            onClick={() => setZoomedPhoto(null)}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// =================================================================
// CONSENT SUMMARY (READ-ONLY) — config-aware, matches Page 3 gating.
// Shows ONLY boxes that are active + visible for the current mode.
// No validation, no checkboxes, no save — display only.
// =================================================================
function ConsentSummary({ currentBookingNumber, mode, lang, t }: { currentBookingNumber: string; mode: string; lang: string; t: any }) {
  const [boxes, setBoxes] = useState<any[]>([]);
  const [consents, setConsents] = useState<Record<string, any>>({});
  const [diversFile, setDiversFile] = useState<any>(null);

  useEffect(() => {
    const bk = (currentBookingNumber || '').trim();
    if (!bk) return;
    let cancelled = false;
    // Box definitions (page_group=page3) — GET is open to charterers (no employee code).
    fetch('/api/master-boxes.php?page_group=page3')
      .then((r) => r.json())
      .then((j) => { if (!cancelled && j && j.success && Array.isArray(j.boxes)) setBoxes(j.boxes); })
      .catch(() => {});
    // Consents for this booking + mode.
    fetch(`/api/box-consents.php?booking_number=${encodeURIComponent(bk)}&mode=${encodeURIComponent(mode || 'in')}`)
      .then((r) => r.json())
      .then((j) => { if (!cancelled && j && j.success && j.consents) setConsents(j.consents); })
      .catch(() => {});
    // divers_report is file_upload (NOT in box_consents) — read its file from Page 3 data.
    getPage3DataHybrid(bk, mode as 'in' | 'out')
      .then((d: any) => { if (!cancelled) setDiversFile(d && d.diversReport ? d.diversReport : null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentBookingNumber, mode]);

  const isTrueFlag = (v: any) => v === true || v === 't' || v === 'true' || v === 1 || v === '1';
  const hiddenForMode = (b: any) => (mode === 'in' ? isTrueFlag(b.hide_checkin) : isTrueFlag(b.hide_checkout));
  const titleOf = (b: any) => b['title_' + lang] || b.title_en || b.box_key;

  // Active + visible for THIS mode only (check-out → all hidden → empty).
  const visible = boxes.filter((b) => b && isTrueFlag(b.active) && !hiddenForMode(b));

  const rows: { key: string; ok: boolean; label: string; extra?: string }[] = [];
  for (const b of visible) {
    if (b.box_key === 'divers_report') {
      // file_upload: show only if a file exists, otherwise skip.
      if (diversFile) rows.push({ key: b.box_key, ok: true, label: titleOf(b), extra: t.fileUploaded || 'file uploaded' });
      continue;
    }
    const ok = !!(consents[b.box_key] && consents[b.box_key].accepted === true);
    rows.push({ key: b.box_key, ok, label: titleOf(b) });
  }

  if (rows.length === 0) return null; // check-out (all hidden) or nothing to show

  return (
    <div className="mb-6 border-2 rounded-xl p-4" style={{ borderColor: brand.blue, background: '#f0f9ff' }}>
      <div className="font-bold mb-2" style={{ color: brand.black }}>
        {t.consentSummaryTitle || 'Customer acceptances'}
      </div>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={r.key} className="text-sm" style={{ color: r.ok ? '#15803d' : '#b91c1c' }}>
            {r.ok ? '✓' : '✗'} {r.label}{r.extra ? ` — ${r.extra}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

// =================================================================
// COMPLETE INVENTORY (CHECK-IN)
// =================================================================
function CompleteInventory({ allItems, t, mode }) {
  if (allItems.length === 0) return null;
  let currentPage = '';
  let currentSection = '';
  
  return (
    <div className="mb-6">
      <div className="rounded-xl border-2 p-4" style={{ borderColor: brand.blue, background: "#f0f9ff" }}>
        <h3 className="font-bold text-xl mb-4 text-center" style={{ color: brand.navy }}>
          {t.completeInventory}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ color: brand.black }}>
            <thead>
              <tr className="border-b-2" style={{ borderColor: brand.navy, backgroundColor: '#e0f2fe' }}>
                <th className="text-left p-2 font-bold">Page</th>
                <th className="text-left p-2 font-bold">Item</th>
                <th className="text-center p-2 font-bold">Qty</th>
                <th className="text-center p-2 font-bold">{mode === 'in' ? 'Check-in' : 'Check-out'}</th>
                <th className="text-right p-2 font-bold">{t.damageRate}</th>
              </tr>
            </thead>
            <tbody>
              {allItems.map((item, idx) => {
                const showSectionHeader = item.page !== currentPage || item.section !== currentSection;
                if (showSectionHeader) {
                  currentPage = item.page;
                  currentSection = item.section;
                }
                const status = mode === 'in' 
                  ? (item.inOk ? '✓' : '✗')
                  : (item.out === 'ok' ? '✓' : item.out === 'not' ? '✗' : item.out === 'missing' ? '✗' : '-');
                const statusColor = status.includes('✓') ? brand.successText : '#ef4444';
                
                return (
                  <React.Fragment key={idx}>
                    {showSectionHeader && (
                      <tr style={{ backgroundColor: '#f1f5f9' }}>
                        <td colSpan="5" className="p-2 font-bold text-xs" style={{ color: brand.navy }}>
                          {item.page} - {item.section}
                        </td>
                      </tr>
                    )}
                    <tr className="border-t" style={{ borderColor: '#e2e8f0' }}>
                      <td className="p-2 text-xs text-gray-600">{item.page}</td>
                      <td className="p-2 font-semibold" style={{ color: brand.black }}>{item.name}</td>
                      <td className="p-2 text-center font-bold">{item.qty}</td>
                      <td className="p-2 text-center font-bold" style={{ color: statusColor }}>{status}</td>
                      <td className="p-2 text-right font-semibold" style={{ color: brand.grey }}>
                        €{(parseFloat(item.price) || 0).toFixed(2)}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =================================================================
// 🔒 EMPLOYEE SIGNATURE WITH LOGIN - LOCKED VERSION
// =================================================================
function EmployeeSignatureWithLogin({ 
  brand, 
  t, 
  signed, 
  setSigned, 
  canvasRef, 
  highlightError, 
  title,
  isEmployee,
  currentEmployee,
  onImageChange,
  onLoginClick
}) {
  useSignatureTouch(canvasRef);
  
  useEffect(() => {
    if (!isEmployee || !canvasRef.current) return;
    
    const currentBooking = localStorage.getItem('currentBooking');
    const mode = localStorage.getItem('currentMode') || 'in';
    
    if (!currentBooking) return;
    
    const signatureKey = `page5_employee_signature_${currentBooking}_${mode}`;
    const saved = localStorage.getItem(signatureKey);
    
    if (saved) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setSigned(true);
      };
      img.src = saved;
    }
  }, [isEmployee, canvasRef, setSigned]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isEmployee) return;
    const ctx = canvas.getContext("2d");
    let drawing = false;
    let x = 0, y = 0;
    
    const getPoint = (e) => {
      const touch = e.touches ? e.touches[0] : e;
      const r = canvas.getBoundingClientRect();
      const scaleX = canvas.width / r.width;
      const scaleY = canvas.height / r.height;
      return {
        x: (touch.clientX - r.left) * scaleX,
        y: (touch.clientY - r.top) * scaleY
      };
    };
    
    const startDrawing = (e) => {
      if (!isEmployee) return;
      drawing = true;
      const point = getPoint(e);
      x = point.x;
      y = point.y;
      setSigned(true);
    };
    
    const draw = (e) => {
      if (!drawing || !isEmployee) return;
      const point = getPoint(e);
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      x = point.x;
      y = point.y;
    };
    
    const stopDrawing = () => {
      drawing = false;
      if (canvas && isEmployee) {
        const imageData = canvas.toDataURL('image/png');
        
        const currentBooking = localStorage.getItem('currentBooking');
        const mode = localStorage.getItem('currentMode') || 'in';
        if (currentBooking) {
          const signatureKey = `page5_employee_signature_${currentBooking}_${mode}`;
          localStorage.setItem(signatureKey, imageData);
          console.log('✅ Employee signature saved to localStorage');
        }
        
        if (onImageChange) {
          onImageChange(imageData);
        }
      }
    };
    
    canvas.addEventListener("mousedown", startDrawing);
    canvas.addEventListener("mousemove", draw);
    window.addEventListener("mouseup", stopDrawing);
    canvas.addEventListener("touchstart", startDrawing, { passive: true });
    canvas.addEventListener("touchmove", draw, { passive: true });
    canvas.addEventListener("touchend", stopDrawing);
    
    return () => {
      canvas.removeEventListener("mousedown", startDrawing);
      canvas.removeEventListener("mousemove", draw);
      window.removeEventListener("mouseup", stopDrawing);
      canvas.removeEventListener("touchstart", startDrawing);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", stopDrawing);
    };
  }, [setSigned, canvasRef, isEmployee, onImageChange]);
  
  const clearSignature = () => {
    if (!isEmployee) {
      alert(t.employeeCodeNeeded);
      if (onLoginClick) onLoginClick();
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
    
    const currentBooking = localStorage.getItem('currentBooking');
    const mode = localStorage.getItem('currentMode') || 'in';
    if (currentBooking) {
      const signatureKey = `page5_employee_signature_${currentBooking}_${mode}`;
      localStorage.removeItem(signatureKey);
    }
  };
  
  return (
    <div 
      className="border-2 rounded-xl p-4 mt-4 transition-all duration-500 relative" 
      style={{ 
        borderColor: highlightError ? brand.errorBorder : (signed ? brand.successBorder : brand.blue), 
        background: highlightError ? brand.errorBg : (signed ? brand.successBg : "#ffffff") 
      }}
    >
      {!isEmployee && (
        <div
          onClick={onLoginClick}
          className="absolute inset-0 bg-red-50 bg-opacity-90 rounded-xl flex items-center justify-center cursor-pointer z-10 hover:bg-opacity-95 transition-all"
        >
          <div className="text-center p-6 bg-white rounded-xl shadow-lg border-3" style={{ borderColor: '#ef4444' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>🔒</div>
            <div className="font-bold text-xl mb-2" style={{ color: '#ef4444' }}>
              {t.employeeCodeRequired}
            </div>
            <div className="text-sm" style={{ color: '#991b1b' }}>
              {t.lang === 'el' ? 'Κλικ για σύνδεση' : 'Click to login'}
            </div>
          </div>
        </div>
      )}
      
      <label className="block font-semibold mb-2" style={{ color: brand.black }}>
        {title} * {isEmployee && currentEmployee && <span className="text-sm font-normal">({currentEmployee.name})</span>}
      </label>
      
      <div style={{
        border: "2px solid",
        borderColor: signed ? brand.successBorder : brand.black,
        background: signed ? brand.successBg : "#ffffff",
        borderRadius: 12,
        padding: 8,
        opacity: isEmployee ? 1 : 0.5,
        pointerEvents: isEmployee ? 'auto' : 'none'
      }}>
        <canvas 
          ref={canvasRef} 
          width={600} 
          height={180} 
          className="w-full max-w-full h-[180px] md:h-[200px] cursor-crosshair"
          style={{ background: signed ? brand.successBg : '#ffffff', touchAction: 'none', transition: 'background 0.3s ease' }}
        />
      </div>
      
      <div className="flex justify-between items-center mt-2">
        {!signed && (
          <div className="text-sm" style={{ color: brand.errorBorder }}>
            {t.signatureRequired}
          </div>
        )}
        <button 
          type="button" 
          onClick={clearSignature} 
          className="px-3 py-1 rounded border ml-auto" 
          style={{ 
            borderColor: brand.black,
            opacity: isEmployee ? 1 : 0.5,
            cursor: isEmployee ? 'pointer' : 'not-allowed'
          }}
        >
          {t.clear}
        </button>
      </div>
    </div>
  );
}

// =================================================================
// END OF PART 2
// Continue with PART 3 (Main Component State & Logic)
// =================================================================
// =================================================================
// PAGE 5 - FINAL CORRECT VERSION - PART 3 OF 4
// =================================================================
// MAIN COMPONENT - STATE & LOGIC WITH ULTRA EMPLOYEE SIGNATURE FIX
// =================================================================
// 🔥🔥🔥 THIS IS THE CRITICAL PART! 🔥🔥🔥
// =================================================================
// PASTE THIS AFTER PART 2
// =================================================================

export default function Page5({ onNavigate }: { onNavigate?: (direction: string) => void }) {
  // Get context data (API is source of truth)
  const contextData = useContext(DataContext);

  const [mode, setMode] = useState('in');
  const isCheckIn = mode === 'in';
  const isCheckOut = mode === 'out';
  const [currentBookingNumber, setCurrentBookingNumber] = useState('');

  // Refs
  const returnRef = useRef(null);
  const termsRef = useRef(null);
  const privacyRef = useRef(null);
  const warningRef = useRef(null);
  const paymentAuthRef = useRef(null);
  const skipperSignatureRef = useRef(null);
  const employeeSignatureRef = useRef(null);
  const skipperCanvasRef = useRef(null);
  const employeeCanvasRef = useRef(null);
  const employeeLoginRef = useRef(null);

  // 🔥 Detect booking change WITHOUT localStorage (App.tsx writes localStorage before we see context)
  const prevBookingRef = useRef(null);
  // 🔧 Per-mode signatures: track mode transitions + whether the current mode's data has loaded.
  const prevModeRef = useRef<string | null>(null);
  const loadedModeRef = useRef<string | null>(null);
  
  const [lang, setLang] = useState(sessionStorage.getItem("yacht_lang") || "en"); // Will be set from context/API
  const navigatePage5 = useNavigate();
  
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  // 🔒 EMPLOYEE STATE
  const [isEmployee, setIsEmployee] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showEmployeePassword, setShowEmployeePassword] = useState(false);
  
  const t = Page5_I18N[lang] || Page5_I18N.en;
  const [allItems, setAllItems] = useState([]);
  const [damageItems, setDamageItems] = useState([]);
  // CHECK-OUT ONLY: per-item τιμές ζημιάς που πληκτρολογεί ο χειριστής (map item_key → τιμή).
  // Ανοίγουν ΜΟΝΟ στα NOT OK (DamageInventory). Persist στο page5Data· εφαρμόζονται στο
  // items_snapshot ΜΟΝΟ όταν mode==='out' → το check-in μένει ανέγγιχτο.
  const [priceOverrides, setPriceOverrides] = useState<Record<string, string>>({});
  const [allPhotos, setAllPhotos] = useState({});
  const [bookingData, setBookingData] = useState({});
  
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [returnAccepted, setReturnAccepted] = useState(false);
  const [warningAccepted, setWarningAccepted] = useState(false);
  const [boxHideMap, setBoxHideMap] = useState<Record<string, { hide_checkin: any; hide_checkout: any }>>({});
  const [paymentAuthAccepted, setPaymentAuthAccepted] = useState(false);
  // 🔧 Card Release (check-out): "no damage found → release pre-auth". Mutually exclusive with paymentAuth.
  const [noDamageReleaseAccepted, setNoDamageReleaseAccepted] = useState(false);
  const [notes, setNotes] = useState("");
  const [skipperSigned, setSkipperSigned] = useState(false);
  const [employeeSigned, setEmployeeSigned] = useState(false);
  const [signatureImage, setSignatureImage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // 🚨 v6.478 — ΜΠΛΟΚΑΡΙΣΤΙΚΟ MODAL ΥΠΟΒΟΛΗΣ. ΔΕΝ κλείνει μόνο του, δεν έχει timer.
  //   kind: 'blocked' | 'failed' | 'sent'
  //   blockers: [{page, text, detail?, ref?}]  warnings: [string]
  const [submitBlock, setSubmitBlock] = useState(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  // 🔓 Ο SERVER κρίνει αν αυτό το check-in είναι ΕΚΚΡΕΜΕΣ (ξεκίνησε & δεν υποβλήθηκε).
  //   Αν ναι, το Submit ΔΟΥΛΕΥΕΙ ακόμη κι αν πέρασε η ημέρα του ναύλου.
  const [serverPending, setServerPending] = useState(false);
  // 🔄 v6.479 — AUTO-RETRY ΜΕΣΑ ΣΤΗ ΣΥΝΕΔΡΙΑ.
  // Το "payload" είναι το ΙΔΙΟ το React state (υπογραφές, checkbox, σημειώσεις,
  // τιμές). ΔΕΝ γράφουμε τίποτα νέο στη συσκευή — η επανάληψη ξανατρέχει το
  // handleSubmit με φρέσκο state. Ζει όσο η σελίδα μένει ανοιχτή· αν κλείσει,
  // τον πιάνει η κόκκινη μπάρα εκκρεμών.
  const [awaitingNetwork, setAwaitingNetwork] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const retryRunningRef = useRef(false);
  const handleSubmitRef = useRef<any>(null);
  const MAX_AUTO_RETRIES = 5;
  const [employeeSignatureImage, setEmployeeSignatureImage] = useState("");
  const [paymentAuthSignatureImage, setPaymentAuthSignatureImage] = useState("");
  
  const [highlightReturn, setHighlightReturn] = useState(false);
  const [highlightTerms, setHighlightTerms] = useState(false);
  const [highlightPrivacy, setHighlightPrivacy] = useState(false);
  const [highlightWarning, setHighlightWarning] = useState(false);
  const [highlightPaymentAuth, setHighlightPaymentAuth] = useState(false);
  const [highlightSkipperSignature, setHighlightSkipperSignature] = useState(false);
  const [highlightEmployeeSignature, setHighlightEmployeeSignature] = useState(false);
  
  const areButtonsLocked = !isEmployee;
  // 🔥 CHARTER WINDOW (v6.196): re-submit allowed within the charter window;
  // permanent lock only after the charter is fully over (Athens local). Replaces
  // the old "permanent lock after first submit" so employees can re-submit while active.
  // 🔓 v6.478 — Ένα check-in που ΞΕΚΙΝΗΣΕ πρέπει να μπορεί να ΤΕΛΕΙΩΣΕΙ.
  // Ο server (pending-checkins.php) είναι ο μόνος κριτής· χωρίς γραμμές στη βάση
  // δεν υπάρχει bypass, άρα δεν ανοίγει τρύπα για ΝΕΑ check-in εκτός ημέρας.
  const windowLocked = isCharterFullyLocked((bookingData as any).checkInDate, (bookingData as any).checkOutDate) && !serverPending;
  
  const percentage = (() => {
    let completed = 0;
    let total = isCheckOut ? 7 : 6;
    
    if (returnAccepted) completed++;
    if (termsAccepted) completed++;
    if (privacyAccepted) completed++;
    if (warningAccepted) completed++;
    if (skipperSigned) completed++;
    
    if (isCheckIn) {
      total = 6;
      if (employeeSigned && isEmployee) completed++;
    }
    
    if (isCheckOut) {
      total = 7;
      if (paymentAuthAccepted || noDamageReleaseAccepted) completed++;
    }
    
    return Math.round((completed / total) * 100);
  })();
  
  // 🔓 v6.478 — ρώτα τον server αν είναι εκκρεμές (ξεκίνησε & δεν υποβλήθηκε).
  useEffect(() => {
    const bn = currentBookingNumber || (typeof window !== 'undefined' ? localStorage.getItem('currentBooking') : '');
    if (!bn) { setServerPending(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          'https://yachtmanagementsuite.com/api/pending-checkins.php?action=check'
          + '&booking_number=' + encodeURIComponent(bn) + '&mode=' + mode + '&t=' + Date.now()
        );
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setServerPending(!!(d && d.success && d.pending));
      } catch (e) {
        console.warn('pending check failed (page5):', e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentBookingNumber, mode, isSubmitted]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  const handleEmployeeLogin = () => {
    setShowLoginModal(true);
  };
  
  const handleEmployeeLogout = () => {
    setIsEmployee(false);
    setCurrentEmployee(null);
    sessionStorage.removeItem('currentEmployee');
  };
  
  useEffect(() => {
    // Load mode from context (API is source of truth)
    const savedMode = contextData?.mode || 'in';
    setMode(savedMode);
  }, [contextData?.mode]);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        // Get booking from context (API is source of truth)
        const currentBooking = contextData?.bookingNumber || localStorage.getItem('currentBooking');
        if (!currentBooking) return;
        setCurrentBookingNumber(currentBooking);
        // 🔧 GATE FIX (#1): open autosave EARLY — before the heavy awaits below — so a throw in any
        // load does NOT leave loadedModeRef null and permanently block ALL page5 autosave.
        loadedModeRef.current = mode;

        // 🔥 FIX: Load inventory items from Pages 2, 3, 4 via API
        console.log('[loadData] ▶ loadAllInventoryItems', currentBooking, mode);
        const inventoryItems = await loadAllInventoryItems(currentBooking, mode as 'in' | 'out', lang);
        console.log('[loadData] ✓ items loaded:', inventoryItems?.length);
        setAllItems(inventoryItems);

        if (mode === 'out') {
          // 🔥 FIX: Filter damaged items - charge ONLY when out === 'not' (NOT OK). Optional inOk:false no longer counts as damage.
          const damaged = inventoryItems.filter(item => item.out === 'not');
          setDamageItems(damaged);
        }

        // 🔥 FIX: Extract photos from inventoryItems (which already has merged media from localStorage)
        // This replaces the deprecated getDamagePhotos/getAllPhotos functions
        // PDF and Email expect an array of URL strings, not media objects
        const extractedPhotos: Record<string, string[]> = {};
        inventoryItems.forEach(item => {
          if (item.media && item.media.length > 0) {
            const itemKey = item.name || item.key || 'unknown';
            // Extract URL strings from media objects (media objects have { mid, type, url } structure)
            const photoUrls = item.media
              .map((m: any) => m?.url || m?.data || (typeof m === 'string' ? m : null))
              .filter(Boolean);
            if (photoUrls.length > 0) {
              extractedPhotos[itemKey] = photoUrls;
              console.log(`📷 Item "${itemKey}" has ${photoUrls.length} photos, first URL starts with: ${photoUrls[0]?.substring(0, 50)}...`);
            }
          }
        });
        console.log(`📷 Extracted photos from ${Object.keys(extractedPhotos).length} items for email/PDF`);
        setAllPhotos(extractedPhotos);

        // 🔥 FIX: First get vessel/skipper data from Page 1 API (source of truth)
        console.log('[loadData] ▶ getPage1DataHybrid');
        const page1Data = await getPage1DataHybrid(currentBooking);
        console.log('[loadData] ✓ page1 loaded');
        console.log('📍 Final Page: Loaded Page 1 data:', page1Data);

        // Use globalBookings from context instead of localStorage
        const globalBookings = contextData?.globalBookings || [];
        const bookingFromContext = globalBookings.find((b: any) =>
          b.bookingNumber === currentBooking || b.code === currentBooking
        );
        const baseData = bookingFromContext || contextData?.data || {};

        // 🔥 FIX: Merge booking info, prioritizing Page 1 data (source of truth for vessel/skipper/dates)
        const mergedData = {
          ...baseData,
          // 🔥 CRITICAL: Explicitly set bookingNumber so PDF and email can use it
          bookingNumber: currentBooking,
          vesselName: page1Data?.vesselName || baseData?.vesselName || baseData?.vessel,
          skipperFirstName: page1Data?.skipperFirstName || baseData?.skipperFirstName,
          skipperLastName: page1Data?.skipperLastName || baseData?.skipperLastName,
          skipperEmail: page1Data?.skipperEmail || baseData?.skipperEmail,
          chartererEmail: page1Data?.chartererEmail || baseData?.chartererEmail,
          skipperPhone: page1Data?.skipperPhone || baseData?.skipperPhone,
          skipperAddress: page1Data?.skipperAddress || baseData?.skipperAddress,
          checkInDate: page1Data?.checkInDate || baseData?.checkInDate,
          checkOutDate: page1Data?.checkOutDate || baseData?.checkOutDate,
        };
        console.log('📍 Final Page: mergedData.bookingNumber =', mergedData.bookingNumber);
        setBookingData(mergedData);

        // Set language from booking data
        if (mergedData?.language) {
          setLang(mergedData.language);
        }

        // 🔥 Try API first for Page 5 data
        let page5Data = null;
        try {
          console.log('[loadData] ▶ getPage5DataHybrid');
          const apiData = await getPage5DataHybrid(currentBooking, mode);
          if (apiData) {
            console.log('✅ Page 5 data loaded from API');
            page5Data = apiData;
          }
        } catch (error) {
          console.warn('⚠️ API load failed, trying localStorage:', error);
        }

        // Fallback to localStorage
        if (!page5Data) {
          const storageKey = mode === 'in' ? `page5DataCheckIn_${currentBooking}` : `page5DataCheckOut_${currentBooking}`;
          const savedPage5Data = localStorage.getItem(storageKey);
          if (savedPage5Data) {
            try {
              page5Data = JSON.parse(savedPage5Data);
            } catch (e) {
              console.error('Error parsing saved Page 5 data:', e);
            }
          }
        }

        if (page5Data) {
          setTermsAccepted(prev => prev || !!page5Data.termsAccepted);
          setPrivacyAccepted(prev => prev || !!page5Data.privacyAccepted);
          setReturnAccepted(prev => prev || !!page5Data.returnAccepted);
          setWarningAccepted(prev => prev || !!page5Data.warningAccepted);
          setPaymentAuthAccepted(page5Data.paymentAuthAccepted || false);
          setNoDamageReleaseAccepted(page5Data.noDamageReleaseAccepted || false);
          setNotes(page5Data.notes || '');
          // 🔧 PER-MODE SIGNATURES: do NOT restore skipper/employee signatures from page5Data.
          //    getPage5DataHybrid → page5.php is per-BOOKING (mode-agnostic) and would seed
          //    check-out with the check-in signature. Skipper is restored by the mode-aware
          //    page5-agreement.php mount-restore below; the employee canvas restores from its
          //    own per-mode localStorage key. paymentAuth (page5-native) stays.
          setPaymentAuthSignatureImage(page5Data.paymentAuthSignatureData || '');
          if (mode === 'out') setPriceOverrides(page5Data.priceOverrides || {});
        } else {
          setPaymentAuthAccepted(false);
          setNoDamageReleaseAccepted(false);
          setNotes('');
          setSignatureImage('');
          setEmployeeSignatureImage('');
          setPaymentAuthSignatureImage('');
        }
        // 🔧 PER-MODE RESTORE from THIS mode's localStorage key only (no cross-mode leak):
        //   - Box flags (paymentAuth / noDamageRelease) [#2]: restored here so they survive reload
        //     even when the mode-agnostic page5.php API row (no such columns) returns nothing.
        //   - Employee signature DRAFT: restored (best-effort).
        //   - Skipper signature DRAFT [#3β]: NOT restored from this shared key (it was the cross-mode
        //     poison vector). The submitted skipper comes from the mode-aware page5-agreement.php
        //     mount-restore instead → check-out stays blank unless it has its own check-out submit.
        try {
          const draftKey = mode === 'in' ? `page5DataCheckIn_${currentBooking}` : `page5DataCheckOut_${currentBooking}`;
          const draft = JSON.parse(localStorage.getItem(draftKey) || '{}');
          if (draft.paymentAuthAccepted) setPaymentAuthAccepted(true);
          if (draft.noDamageReleaseAccepted) setNoDamageReleaseAccepted(true);
          if (draft.employeeSignatureData) {
            setEmployeeSignatureImage(prev => prev || draft.employeeSignatureData);
            setEmployeeSigned(true);
          }
        } catch (e) { /* ignore malformed draft */ }
      } catch (e) {
        console.error("[loadData] ✗ THREW — Error loading booking data:", e);
      }
    };
    loadData();
    window.addEventListener('focus', loadData);
    window.addEventListener('pageshow', loadData);
    return () => {
      window.removeEventListener('focus', loadData);
      window.removeEventListener('pageshow', loadData);
    };
  }, [lang, mode, contextData?.bookingNumber]);

  // 🔥 Reset data if booking number changed (useRef-based — no localStorage; multi-tenant safe)
  useEffect(() => {
    const bn = contextData?.bookingNumber || '';
    if (!bn) return;
    if (prevBookingRef.current !== null && prevBookingRef.current !== bn) {
      console.log(`🔄 FinalPage booking changed: ${prevBookingRef.current} → ${bn} - RESETTING all data`);
      setTermsAccepted(false);
      setPrivacyAccepted(false);
      setReturnAccepted(false);
      setWarningAccepted(false);
      setPaymentAuthAccepted(false);
      setNoDamageReleaseAccepted(false);
      setSkipperSigned(false);
      setEmployeeSigned(false);
      setSignatureImage("");
      setEmployeeSignatureImage("");
      setPaymentAuthSignatureImage("");
      setNotes("");
    }
    prevBookingRef.current = bn;
  }, [contextData?.bookingNumber]);

  // 🔧 PER-MODE SIGNATURES: clear signatures when the mode actually changes so check-out starts
  // blank. The mode-aware mount-restore / employee component re-apply the CURRENT mode's
  // signature. Pauses autosave (loadedModeRef=null) until the new mode's data has loaded, so the
  // transient previous-mode signature is never written into this mode's store.
  useEffect(() => {
    if (prevModeRef.current !== null && prevModeRef.current !== mode) {
      // Clear signatures on mode switch (cross-mode protection). Do NOT clear loadedModeRef:
      // loadData re-opens it early for the new mode; clearing here would re-block autosave (#1).
      setSignatureImage('');
      setEmployeeSignatureImage('');
      setSkipperSigned(false);
      setEmployeeSigned(false);
    }
    prevModeRef.current = mode;
  }, [mode]);

  // === BOX CONSENTS from DB (boxes now live on Page 3; consent follows box_key per booking+mode) ===
  // Only sets TRUE (never clears) so it cannot break existing page5Data-based loads.
  useEffect(() => {
    if (!currentBookingNumber) return;
    let cancelled = false;
    const m = (mode === 'out') ? 'out' : 'in';
    fetch(`/api/box-consents.php?booking_number=${encodeURIComponent(currentBookingNumber)}&mode=${encodeURIComponent(m)}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled || !j || !j.success || !j.consents) return;
        const cs = j.consents;
        if (cs.return_acknowledgement && cs.return_acknowledgement.accepted) setReturnAccepted(true);
        if (cs.terms_conditions && cs.terms_conditions.accepted) setTermsAccepted(true);
        if (cs.privacy_consent && cs.privacy_consent.accepted) setPrivacyAccepted(true);
        if (cs.warning_notice && cs.warning_notice.accepted) setWarningAccepted(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBookingNumber, mode]);

  // 🔧 FIX (Σύμπτωμα 2 + Σύμπτωμα 3 για submitted bookings):
  // Σε mount/booking-change/mode-change, διάβασε το page5_agreement row για αυτή τη (booking, mode).
  // - Αν υπάρχει & (email_sent=true ή items_snapshot≠null) → setIsSubmitted(true):
  //   πραγματικός submit έγινε → κλειδώνει PDF/Submit μετά από Back→Next.
  //   (Το terms_accepted ΔΕΝ είναι αξιόπιστο — γράφεται από auto-save όταν ο
  //   χρήστης τσεκάρει το checkbox, χωρίς να έχει γίνει submit.)
  // - Αν έχει skipper_signature → επανέφερε το base64 στο state ώστε το canvas να ξαναζωγραφίσει
  //   την υπογραφή του πελάτη που υπέγραψε κατά το submit (νομικά frozen, δεν χάνεται).
  //   Η company/employee signature ΔΕΝ αγγίζεται εδώ — δουλεύει ήδη μέσω άλλου path.
  useEffect(() => {
    if (!currentBookingNumber) return;
    let cancelled = false;
    (async () => {
      try {
        const ag = await getPage5Agreement(currentBookingNumber);
        const row = mode === 'in' ? ag?.checkIn : ag?.checkOut;
        if (cancelled) return;

        if (row && (row.email_sent === true || row.items_snapshot != null)) {
          setIsSubmitted(true);
        }

        // Ο SERVER ΕΙΝΑΙ Η ΑΥΘΕΝΤΙΑ. Αν έχει υπογραφή, ισχύει ΜΟΝΟ αυτή.
        const serverSig = (row && typeof row.skipper_signature === 'string'
          && row.skipper_signature.length > 10) ? row.skipper_signature : null;

        if (serverSig) {
          setSignatureImage(serverSig);
          setSkipperSigned(true);
          // Ο server την έχει → η τοπική γέφυρα δεν χρειάζεται πια.
          try { localStorage.removeItem(`page5_skipper_signature_${currentBookingNumber}_${mode}`); } catch (e2) {}
          return;
        }

        // 🌉 ΜΟΝΟ όταν ο server ΔΕΝ έχει υπογραφή (π.χ. έπεσε το δίκτυο στο submit).
        // Το κλειδί περιέχει mode → το check-out ΔΕΝ βλέπει ποτέ την υπογραφή του check-in.
        try {
          const bridged = localStorage.getItem(`page5_skipper_signature_${currentBookingNumber}_${mode}`);
          if (bridged && bridged.length > 100) {
            setSignatureImage(bridged);
            setSkipperSigned(true);
            console.log('🌉 Skipper signature restored from local bridge (server had none), mode=' + mode);
          }
        } catch (e3) { /* ignore */ }
      } catch (e) {
        console.warn('Page 5 agreement mount-restore failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [currentBookingNumber, mode]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      // 🔧 PER-MODE: skip persisting until the current mode's data has finished loading, so a
      // transient signature from the previous mode is never written into this mode's store.
      if (loadedModeRef.current !== mode) return;
      const page5Data = {
        priceOverrides, // CHECK-OUT per-item damage prices (persist σε reload πριν το submit)
        termsAccepted,
        privacyAccepted,
        returnAccepted,
        warningAccepted,
        paymentAuthAccepted,
        noDamageReleaseAccepted,
        notes,
        // 🔧 FIX (Σύμπτωμα 3): page5.php's POST/PUT διαβάζει $input['skipperSignature']
        //    και το γράφει στη στήλη skipper_signature. Πριν στέλναμε boolean εδώ
        //    και το base64 σε skipperSignatureData που το API αγνοούσε → DB έμενε άδεια.
        //    Τώρα στέλνουμε το base64 στο σωστό field name ώστε να σώζεται πραγματικά.
        //    Η company/employee signature ΔΕΝ αγγίζεται (λειτουργεί ήδη μέσω handleSubmit).
        skipperSignature: signatureImage,
        employeeSignature: employeeSigned,
        employeeSignatureData: employeeSignatureImage,
        paymentAuthSignatureData: paymentAuthSignatureImage,
        employeeName: currentEmployee?.name || '',
        timestamp: new Date().toISOString()
      };
      const storageKey = mode === 'in' ? `page5DataCheckIn_${currentBookingNumber}` : `page5DataCheckOut_${currentBookingNumber}`;
      if (currentBookingNumber) {
        localStorage.setItem(storageKey, JSON.stringify(page5Data));
        // 🔥 Also save to API
        try {
          await savePage5DataHybrid(currentBookingNumber, page5Data, mode);
          console.log('💾 Page 5 auto-saved to API');
        } catch (error) {
          console.warn('⚠️ Page 5 API auto-save failed:', error);
        }
      }
    }, 2000); // Increased to 2 seconds to reduce API calls
    return () => clearTimeout(timer);
  }, [priceOverrides, termsAccepted, privacyAccepted, returnAccepted, warningAccepted, paymentAuthAccepted, noDamageReleaseAccepted, notes, skipperSigned, employeeSigned, signatureImage, employeeSignatureImage, paymentAuthSignatureImage, currentEmployee, mode, currentBookingNumber]);
  
  const handleSaveDraft = () => {
    alert(t.dataSaved);
  };
  
  // === BOX HIDE FLAGS (per mode) ===
  // Loaded from the same master-boxes.php that DynamicBox uses, so validateForm knows
  // which consent boxes are hidden in the current mode (and therefore not required).
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/master-boxes.php?page_group=page3`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled || !j || !j.success || !Array.isArray(j.boxes)) return;
        const map: Record<string, { hide_checkin: any; hide_checkout: any }> = {};
        j.boxes.forEach((b: any) => {
          if (b && b.box_key) map[b.box_key] = { hide_checkin: b.hide_checkin, hide_checkout: b.hide_checkout };
        });
        setBoxHideMap(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // A consent box blocks submit only if it is VISIBLE in the current mode.
  // Hidden in current mode (hide_checkin in check-in / hide_checkout in check-out) -> not required.
  // Fail-safe: unknown box (flags not loaded / network fail) -> required (return true), so a
  // mandatory consent is never bypassed by accident.
  const isBoxRequired = (boxKey: string) => {
    const f = boxHideMap[boxKey];
    if (!f) return true;
    const hidden = (mode === 'out')
      ? (f.hide_checkout === true || f.hide_checkout === 't')
      : (f.hide_checkin === true || f.hide_checkin === 't');
    return !hidden;
  };

  const validateForm = () => {
    console.log('SUBMITCHECKS', JSON.stringify({ returnAccepted, termsAccepted, privacyAccepted, warningAccepted, skipperSigned, employeeSigned, isEmployee, isCheckIn, isCheckOut, paymentAuthAccepted, noDamageReleaseAccepted }));
    setHighlightReturn(false);
    setHighlightTerms(false);
    setHighlightPrivacy(false);
    setHighlightWarning(false);
    setHighlightPaymentAuth(false);
    setHighlightSkipperSignature(false);
    setHighlightEmployeeSignature(false);
    
    // ORPHAN consent checks removed: return_acknowledgement, terms_conditions, privacy_consent,
    // warning_notice all moved to page_group=page3 and are now enforced dynamically there
    // (config-aware). They are not rendered on page5 (only empty ref divs), so re-checking here
    // scrolled to empty divs and could silently block submit. Consents now live on page3 only.

    if (!skipperSigned) {
      setHighlightSkipperSignature(true);
      skipperSignatureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightSkipperSignature(false), 3000);
      return false;
    }
    
    if (isCheckIn && (!employeeSigned || !isEmployee)) {
      setHighlightEmployeeSignature(true);
      employeeSignatureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightEmployeeSignature(false), 3000);
      return false;
    }
    
    if (isCheckOut && !paymentAuthAccepted && !noDamageReleaseAccepted) {
      setHighlightPaymentAuth(true);
      paymentAuthRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightPaymentAuth(false), 3000);
      return false;
    }
    
    return true;
  };
  
  // =================================================================
  // 🔥🔥🔥 ULTRA FIXED handleGeneratePDF - 3 ATTEMPTS! 🔥🔥🔥
  // =================================================================
  // ⏱️ CHECK-IN DURATION (Europe/Athens ώρα εμφανίζεται· εδώ υπολογισμός σε λεπτά). ΜΟΝΟ mode=in.
  const computeCheckInDuration = async () => {
    if (mode !== 'in') return null;
    try {
      const p4 = await getPage4DataHybrid(currentBookingNumber, 'in');
      const startIso = p4 && p4.checkInStartedAt;
      if (!startIso) return null;
      const startMs = new Date(startIso).getTime();
      const endMs = Date.now();
      const durationMinutes = Math.max(0, Math.round((endMs - startMs) / 60000));
      const h = Math.floor(durationMinutes / 60);
      const m = durationMinutes % 60;
      // i18n: duration text στη γλωσσα του πελατη (fallback en). {D} = localized διαρκεια.
      const DUR_UNITS: Record<string, [string, string]> = {
        el: ['ώρες', 'λεπτά'], en: ['hours', 'minutes'], it: ['ore', 'minuti'],
        de: ['Stunden', 'Minuten'], ru: ['часов', 'минут'], fr: ['heures', 'minutes'],
        ro: ['ore', 'minute'], pl: ['godzin', 'minut'], he: ['שעות', 'דקות'], es: ['horas', 'minutos'],
      };
      const DUR_TPL: Record<string, string> = {
        el: 'Η διαδικασία check-in διήρκεσε {D} από τον τεχνικό της εταιρείας',
        en: 'The check-in process took {D} with the company technician',
        it: 'La procedura di check-in è durata {D} con il tecnico della compagnia',
        de: 'Der Check-in-Vorgang dauerte {D} mit dem Techniker des Unternehmens',
        ru: 'Процесс регистрации занял {D} с техником компании',
        fr: "La procédure d'enregistrement a duré {D} avec le technicien de la compagnie",
        ro: 'Procesul de check-in a durat {D} cu tehnicianul companiei',
        pl: 'Proces odprawy trwał {D} z technikiem firmy',
        he: "תהליך הצ'ק-אין נמשך {D} עם טכנאי החברה",
        es: 'El proceso de check-in duró {D} con el técnico de la compañía',
      };
      const durLang = DUR_TPL[lang] ? lang : 'en';
      const [hUnit, mUnit] = DUR_UNITS[durLang];
      const dur = h > 0 ? (h + ' ' + hUnit + ' ' + m + ' ' + mUnit) : (m + ' ' + mUnit);
      const text = DUR_TPL[durLang].replace('{D}', dur);
      return { checkInStartedAt: startIso, checkInCompletedAt: new Date(endMs).toISOString(), checkInDurationMinutes: durationMinutes, checkInDurationText: text };
    } catch (e) { return null; }
  };

  const handleGeneratePDF = async () => {
    if (!isEmployee) {
      alert(t.employeeCodeNeeded);
      handleEmployeeLogin();
      return;
    }
    
    if (!validateForm()) return;
    
    try {
      console.log('🔍 ===== PDF GENERATION START =====');
      console.log('🔍 Mode:', mode);
      console.log('🔍 IsCheckIn:', isCheckIn);
      console.log('🔍 currentBookingNumber:', currentBookingNumber);
      
      const skipperSignatureData = signatureImage || null;
      console.log('🔍 Skipper signature:', skipperSignatureData ? 'HAS DATA' : 'NO DATA');
      
      // 🔥 GET EMPLOYEE SIGNATURE - MULTIPLE ATTEMPTS! (FOR BOTH CHECK-IN AND CHECK-OUT!)
      let employeeSignatureData = null;
      
      // 🔥 ALWAYS GET EMPLOYEE SIGNATURE (not just check-in!)
      console.log('🔥 Getting employee signature for mode:', mode);
        
        // ATTEMPT 1: From canvas REF
        if (employeeCanvasRef && employeeCanvasRef.current) {
          try {
            const canvas = employeeCanvasRef.current;
            console.log('🔍 Canvas found:', canvas.width, 'x', canvas.height);
            
            const ctx = canvas.getContext('2d');
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const pixels = imageData.data;
            
            let hasDrawing = false;
            for (let i = 0; i < pixels.length; i += 4) {
              if (pixels[i + 3] > 0) {
                hasDrawing = true;
                break;
              }
            }
            
            if (hasDrawing) {
              const canvasDataURL = canvas.toDataURL('image/png');
              console.log('🔍 Canvas has drawing - length:', canvasDataURL.length);
              employeeSignatureData = await compressSignature(canvasDataURL);
              console.log('✅ ATTEMPT 1 SUCCESS - Got signature from canvas!');
            } else {
              console.log('⚠️ ATTEMPT 1 FAILED - Canvas is empty');
            }
          } catch (e) {
            console.error('❌ ATTEMPT 1 ERROR:', e);
          }
        } else {
          console.log('⚠️ ATTEMPT 1 SKIPPED - No canvas ref');
        }
        
        // ATTEMPT 2: From localStorage
        if (!employeeSignatureData && currentBookingNumber) {
          const signatureKey = `page5_employee_signature_${currentBookingNumber}_${mode}`;
          console.log('🔍 ATTEMPT 2 - Looking in localStorage for key:', signatureKey);
          const saved = localStorage.getItem(signatureKey);
          if (saved && saved.length > 100) {
            employeeSignatureData = saved;
            console.log('✅ ATTEMPT 2 SUCCESS - Got signature from localStorage! Length:', saved.length);
          } else {
            console.log('⚠️ ATTEMPT 2 FAILED - No signature in localStorage');
          }
        }
        
        // ATTEMPT 3: From state variable
        if (!employeeSignatureData && employeeSignatureImage) {
          console.log('🔍 ATTEMPT 3 - Using state variable');
          if (employeeSignatureImage.length > 100) {
            employeeSignatureData = employeeSignatureImage;
            console.log('✅ ATTEMPT 3 SUCCESS - Got signature from state! Length:', employeeSignatureImage.length);
          } else {
            console.log('⚠️ ATTEMPT 3 FAILED - State variable too short');
          }
        }
        
        if (employeeSignatureData) {
          console.log('🔥🔥🔥 FINAL: Employee signature captured! Length:', employeeSignatureData.length);
        } else {
          console.log('❌❌❌ FINAL: NO EMPLOYEE SIGNATURE!');
        }
      
      const page5OnlyData = {
        agreements: {
          terms: termsAccepted,
          privacy: privacyAccepted,
          return: returnAccepted,
          warning: warningAccepted,
          paymentAuth: isCheckOut ? paymentAuthAccepted : false,
          noDamageRelease: isCheckOut ? noDamageReleaseAccepted : false
        },
        warningAccepted: warningAccepted,
        paymentAuthAccepted: isCheckOut ? paymentAuthAccepted : false,
        noDamageReleaseAccepted: isCheckOut ? noDamageReleaseAccepted : false,
        notes: notes,
        skipperSignature: skipperSignatureData,
        employeeSignature: employeeSignatureData,
        allItems: isCheckIn ? allItems : damageItems,
        photos: allPhotos,
        timestamp: new Date().toISOString()
      };
      
      const _durP = await computeCheckInDuration();
      if (_durP) Object.assign(page5OnlyData, _durP);
      console.log('📦 Data to PDF Generator:');
      console.log('   - skipperSignature:', page5OnlyData.skipperSignature ? 'YES' : 'NO');
      console.log('   - employeeSignature:', page5OnlyData.employeeSignature ? 'YES' : 'NO');

      await loadPdfFonts(); // εξασφαλιζει DejaVuSans (Unicode) πριν το PDF render
      const pdfDoc = generateLuxuryPDF(bookingData, mode, page5OnlyData, lang, { isPage5: true });
      
      if (pdfDoc) {
        const fileName = `${mode === 'in' ? 'check-in' : 'check-out'}-page5-${bookingData.bookingNumber || 'draft'}-${Date.now()}.pdf`;
        pdfDoc.save(fileName);
        alert(t.pdfGenerated);
      }
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      alert('Error generating PDF: ' + error.message);
    }
  };
  
  // Δικτυακή αποτυχία; (offline, DNS, timeout, αποτυχία fetch)
  const isNetworkError = (err: any) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
    const m = String(err?.message || err || '').toLowerCase();
    return m.includes('failed to fetch') || m.includes('networkerror')
      || m.includes('load failed') || m.includes('network request failed')
      || m.includes('err_internet_disconnected') || m.includes('err_network');
  };

  // ΕΝΙΑΙΑ διαχείριση αποτυχίας υποβολής.
  //   δικτυακή  → ΟΥΡΑ + μπάνερ «θα ξαναδοκιμάσω»
  //   οτιδήποτε άλλο → κόκκινο modal, χειροκίνητη επανάληψη
  // Σε ΚΑΘΕ περίπτωση: isSubmitted = false. ΠΟΤΕ πράσινο πριν απαντήσει ο server.
  const failSubmit = (errText: string, warnings: string[], err?: any) => {
    setIsSubmitting(false);
    setIsSubmitted(false);
    setSubmitFailed(true);
    if (isNetworkError(err)) {
      setAwaitingNetwork(true);
      setSubmitBlock({ kind: 'waiting', blockers: [], warnings, error: errText });
    } else {
      setAwaitingNetwork(false);
      setSubmitBlock({ kind: 'failed', blockers: [], warnings, error: errText });
    }
  };

  const handleSubmit = async (opts: any = {}) => {
    const isRetry = opts && opts.isRetry === true;

    // 🔁 ΑΠΟΦΥΓΗ ΔΙΠΛΩΝ EMAIL — μόνο στο auto-retry.
    // Ρώτα ΠΡΩΤΑ τον server: αν το email_sent είναι ήδη true, η προηγούμενη
    // προσπάθεια είχε πετύχει και χάθηκε μόνο η απάντηση. ΔΕΝ ξαναστέλνουμε.
    // (Στο ΧΕΙΡΟΚΙΝΗΤΟ submit ΔΕΝ ισχύει — ο υπάλληλος μπορεί να θέλει επανυποβολή.)
    if (isRetry && currentBookingNumber) {
      try {
        const ag0 = await getPage5Agreement(currentBookingNumber);
        const row0 = mode === 'in' ? ag0?.checkIn : ag0?.checkOut;
        if (row0 && row0.email_sent === true) {
          console.log('🔁 auto-retry: email_sent ήδη true — ΔΕΝ ξαναστέλνω');
          setAwaitingNetwork(false);
          setRetryAttempt(0);
          setIsSubmitting(false);
          setSubmitFailed(false);
          setIsSubmitted(true);
          try { localStorage.removeItem(`page5_skipper_signature_${currentBookingNumber}_${mode}`); } catch (e) {}
          setSubmitBlock({ kind: 'sent', blockers: [], warnings: [
            lang === 'el'
              ? 'Η προηγούμενη προσπάθεια είχε ΗΔΗ σταλεί — δεν στάλθηκε δεύτερο email.'
              : 'The previous attempt had ALREADY been sent — no duplicate email.',
          ] });
          window.dispatchEvent(new CustomEvent('pendingCheckinsChanged'));
          return;
        }
      } catch (e) {
        // Δεν απάντησε ο server → συνέχισε κανονικά· αν ξαναπέσει, ξαναμπαίνει σε ουρά.
      }
    }

    if (!isEmployee) {
      alert(t.employeeCodeNeeded);
      handleEmployeeLogin();
      return;
    }
    
    const checkInDur = await computeCheckInDuration();

    // =====================================================================
    // 🚨 v6.478 — ΠΛΗΡΗΣ, ΡΗΤΟΣ ΕΛΕΓΧΟΣ ΠΡΙΝ ΤΟ SUBMIT
    // ΤΟ ΠΑΛΙΟ BUG: το p4Arrays έψαχνε keys ('items','navItems','safetyItems'...)
    // που ΔΕΝ υπάρχουν στο page4_checklist — εκεί υπάρχει ΜΟΝΟ
    // `technicalCheckItems`. Άρα 54/55 ελλιπή items της Σελίδας 4 περνούσαν
    // ΑΟΡΑΤΑ, το validateForm() γύριζε false στην υπογραφή και ο υπάλληλος
    // έβλεπε ΜΟΝΟ κόκκινο πλαίσιο 3 δευτ. — καμία εξήγηση.
    // ΤΩΡΑ: γενικό σκανάρισμα (countPage) + modal που ΛΕΕΙ ΑΚΡΙΒΩΣ ΤΙ ΛΕΙΠΕΙ
    // και ΔΕΝ κλείνει μόνο του.
    // =====================================================================
    const bookingNumForValidation = currentBookingNumber || localStorage.getItem('currentBooking');
    const blockers: any[] = [];
    const runtimeWarnings: string[] = [];

    if (bookingNumForValidation) {
      // ---- Σελίδα 1: υποχρεωτικά πεδία ----
      try {
        const p1 = await getPage1DataHybrid(bookingNumForValidation);
        if (p1) {
          const requiredP1 = [
            { key: 'vesselName', el: 'Όνομα Σκάφους', en: 'Vessel Name' },
            { key: 'skipperFirstName', el: 'Όνομα Σκίπερ', en: 'Skipper First Name' },
            { key: 'checkInDate', el: 'Ημερομηνία Check-in', en: 'Check-in Date' },
          ];
          for (const f of requiredP1) {
            if (!p1[f.key] || String(p1[f.key]).trim() === '') {
              blockers.push({
                page: 1,
                text: lang === 'el' ? ('Σελίδα 1 — λείπει: ' + f.el) : ('Page 1 — missing: ' + f.en),
              });
            }
          }
        } else {
          runtimeWarnings.push(lang === 'el'
            ? 'Η Σελίδα 1 δεν φορτώθηκε από τον server — ΔΕΝ ελέγχθηκε.'
            : 'Page 1 did not load from the server — NOT validated.');
        }
      } catch (err: any) {
        runtimeWarnings.push((lang === 'el' ? 'Σφάλμα ελέγχου Σελίδας 1: ' : 'Page 1 validation error: ')
          + (err?.message || err));
      }

      // ---- Κλειδιά ΕΝΕΡΓΑ ΓΙΑ ΑΥΤΟ ΤΟ ΣΚΑΦΟΣ (yacht_items) ----
      // Τα items ΔΙΑΦΕΡΟΥΝ ανά σκάφος. Χωρίς αυτό, ναύλοι του Maria 1 (19 ανενεργά
      // items) έβγαζαν ψευδώς «λείπει». Αν δεν φορτωθεί → ΔΕΝ μπλοκάρουμε.
      let vKeys: any = null;
      try {
        const vName = String((bookingData as any)?.vesselName || '').replace(/^.*-\s*/, '').trim();
        const vessels = await getVessels();
        const v = (vessels || []).find((x: any) =>
          String(x?.name || '').toLowerCase() === vName.toLowerCase());
        if (v && v.id) vKeys = vesselKeySet(await getYachtItems(v.id));
      } catch (err: any) {
        vKeys = null;
      }
      if (!vKeys) {
        runtimeWarnings.push(lang === 'el'
          ? 'Δεν φορτώθηκε η λίστα ειδών του σκάφους — οι ελλείψεις ΔΕΝ μπλοκάρουν.'
          : 'Vessel item list did not load — missing items do NOT block.');
      }

      // ---- Σελίδες 2 / 3 / 4: ΓΕΝΙΚΟ σκανάρισμα ΟΛΩΝ των item sections ----
      const pageDefs = [
        { page: 2, key: 'page2', getter: getPage2DataHybrid },
        { page: 3, key: 'page3', getter: getPage3DataHybrid },
        { page: 4, key: 'page4', getter: getPage4DataHybrid },
      ];
      for (const def of pageDefs) {
        let data: any = null;
        try {
          data = await def.getter(bookingNumForValidation, mode);
        } catch (err: any) {
          runtimeWarnings.push((lang === 'el' ? 'Σφάλμα φόρτωσης ' : 'Load error ')
            + pageLabel(def.key, lang) + ': ' + (err?.message || err));
          continue;
        }
        if (!data) {
          runtimeWarnings.push(pageLabel(def.key, lang) + (lang === 'el'
            ? ' — δεν βρέθηκαν δεδομένα στον server, ΔΕΝ ελέγχθηκε.'
            : ' — no data on the server, NOT validated.'));
          continue;
        }
        // ΚΑΝΟΝΕΣ ΕΙΔΩΝ (master_items): πετάει τα ανενεργά και όσα ισχύουν ΜΟΝΟ
        // στο άλλο mode (checkout_only κ.λπ.). Χωρίς αυτό, σωστά ολοκληρωμένοι
        // ναύλοι έβγαζαν ψευδώς «λείπει» — π.χ. ο 195 έδειχνε 4 ανύπαρκτα.
        let rules: any = undefined;
        try {
          const master = await getMasterItems(undefined, def.key);
          rules = buildRules(master || [], mode as any, vKeys);
        } catch (err: any) {
          rules = undefined;
        }
        const rulesOk = !!(rules && rules.loaded);

        const pc = countPage(def.key as any, data, mode as any, rules);
        if (pc.missing > 0 && !rulesOk) {
          // FAIL-OPEN: χωρίς κανόνες ΔΕΝ μπλοκάρουμε — δεν σταματάμε δουλειά
          // επειδή έπεσε ένα API. Το λέμε όμως ρητά.
          runtimeWarnings.push(pageLabel(def.key, lang) + (lang === 'el'
            ? ': δεν φορτώθηκαν οι κανόνες ειδών — ΔΕΝ μπλόκαρε (πιθανές '
            : ': item rules did not load — NOT blocked (possibly ')
            + pc.missing + (lang === 'el' ? ' ελλείψεις).' : ' missing).'));
        } else if (pc.missing > 0) {
          const detail = pc.sections
            .filter((sec: any) => sec.missing > 0)
            .sort((a: any, b: any) => b.missing - a.missing)
            .map((sec: any) => sectionLabel(sec.key, lang) + ' ' + sec.done + '/' + sec.total)
            .join(' · ');
          blockers.push({
            page: def.page,
            text: lang === 'el'
              ? ('Λείπουν ' + pc.missing + ' στοιχεία στη ' + pageLabel(def.key, lang)
                 + ' (' + pc.done + '/' + pc.total + ')')
              : (pc.missing + ' items missing on ' + pageLabel(def.key, lang)
                 + ' (' + pc.done + '/' + pc.total + ')'),
            detail,
          });
        }
      }
    } else {
      runtimeWarnings.push(lang === 'el'
        ? 'Δεν βρέθηκε αριθμός ναύλου — ΔΕΝ έγινε έλεγχος σελίδων.'
        : 'No booking number — pages were NOT validated.');
    }

    // ---- Υπογραφές / συναινέσεις (πρώην validateForm: σιωπηλό return false) ----
    if (!skipperSigned) {
      setHighlightSkipperSignature(true);
      blockers.push({
        page: 5, ref: 'skipper',
        text: lang === 'el' ? 'Λείπει η υπογραφή πελάτη / σκίπερ' : 'Customer / skipper signature is missing',
      });
    }
    if (isCheckIn && (!employeeSigned || !isEmployee)) {
      setHighlightEmployeeSignature(true);
      blockers.push({
        page: 5, ref: 'employee',
        text: lang === 'el' ? 'Λείπει η υπογραφή υπαλλήλου' : 'Employee signature is missing',
      });
    }
    if (isCheckOut && !paymentAuthAccepted && !noDamageReleaseAccepted) {
      setHighlightPaymentAuth(true);
      blockers.push({
        page: 5, ref: 'payment',
        text: lang === 'el'
          ? 'Λείπει η εξουσιοδότηση πληρωμής / δήλωση μη ζημιάς'
          : 'Payment authorization / no-damage release is missing',
      });
    }

    if (blockers.length > 0) {
      // ΔΕΝ υποβλήθηκε. Το κουμπί ΜΕΝΕΙ ενεργό, το modal ΔΕΝ κλείνει μόνο του.
      setIsSubmitting(false);
      setIsSubmitted(false);
      setSubmitFailed(true);
      setSubmitBlock({ kind: 'blocked', blockers, warnings: runtimeWarnings });
      return;
    }

    setIsSubmitting(true);
    setSubmitFailed(false);
    setSubmitBlock(null);

    try {
      console.log('📝 handleSubmit - Starting signature capture...');
      console.log('📝 handleSubmit - skipperSigned:', skipperSigned);
      console.log('📝 handleSubmit - skipperCanvasRef.current:', !!skipperCanvasRef.current);
      console.log('📝 handleSubmit - signatureImage length:', signatureImage?.length || 0);

      let skipperSignatureData = null;

      // Try to get skipper signature from canvas first
      if (skipperSigned && skipperCanvasRef.current) {
        try {
          skipperSignatureData = await compressSignature(skipperCanvasRef.current.toDataURL('image/png'));
          console.log('📝 handleSubmit - Skipper sig from canvas, length:', skipperSignatureData?.length || 0);
        } catch (e) {
          console.error("❌ Error compressing skipper signature:", e);
        }
      }

      // Fallback to signatureImage state (like employee signature logic)
      if (!skipperSignatureData && signatureImage && signatureImage.length > 100) {
        skipperSignatureData = signatureImage;
        console.log('📝 handleSubmit - Skipper sig from signatureImage state, length:', skipperSignatureData?.length || 0);
      }

      // Fallback to localStorage
      if (!skipperSignatureData && currentBookingNumber) {
        const signatureKey = `page5_skipper_signature_${currentBookingNumber}_${mode}`;
        const saved = localStorage.getItem(signatureKey);
        if (saved && saved.length > 100) {
          skipperSignatureData = saved;
          console.log('📝 handleSubmit - Skipper sig from localStorage, length:', skipperSignatureData?.length || 0);
        }
      }

      console.log('📝 handleSubmit - Final skipperSignatureData:', skipperSignatureData ? `${skipperSignatureData.substring(0, 50)}... (${skipperSignatureData.length} chars)` : 'null');
      
      // 🔥 SAME LOGIC AS PDF GENERATION - FOR ALL MODES!
      let employeeSignatureData = null;
      
      // Get employee signature for both check-in and check-out
      if (employeeCanvasRef && employeeCanvasRef.current) {
          try {
            const canvas = employeeCanvasRef.current;
            const ctx = canvas.getContext('2d');
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const pixels = imageData.data;
            
            let hasDrawing = false;
            for (let i = 0; i < pixels.length; i += 4) {
              if (pixels[i + 3] > 0) {
                hasDrawing = true;
                break;
              }
            }
            
            if (hasDrawing) {
              const canvasDataURL = canvas.toDataURL('image/png');
              employeeSignatureData = await compressSignature(canvasDataURL);
            }
          } catch (e) {
            console.error('❌ Canvas error:', e);
          }
        }
        
        if (!employeeSignatureData && currentBookingNumber) {
          const signatureKey = `page5_employee_signature_${currentBookingNumber}_${mode}`;
          const saved = localStorage.getItem(signatureKey);
          if (saved && saved.length > 100) {
            employeeSignatureData = saved;
          }
        }
        
        if (!employeeSignatureData && employeeSignatureImage && employeeSignatureImage.length > 100) {
          employeeSignatureData = employeeSignatureImage;
          console.log('📝 handleSubmit - Employee sig from employeeSignatureImage state');
        }

      console.log('📝 handleSubmit - Final employeeSignatureData:', employeeSignatureData ? `${employeeSignatureData.substring(0, 50)}... (${employeeSignatureData.length} chars)` : 'null');
      console.log('📝 handleSubmit - Sending to email: skipper=', !!skipperSignatureData, 'employee=', !!employeeSignatureData);

      const page5AdditionalData = {
        agreements: {
          terms: termsAccepted,
          privacy: privacyAccepted,
          return: returnAccepted,
          warning: warningAccepted,
          paymentAuth: isCheckOut ? paymentAuthAccepted : false,
          noDamageRelease: isCheckOut ? noDamageReleaseAccepted : false
        },
        warningAccepted: warningAccepted,
        paymentAuthAccepted: isCheckOut ? paymentAuthAccepted : false,
        noDamageReleaseAccepted: isCheckOut ? noDamageReleaseAccepted : false,
        notes: notes,
        skipperSignature: skipperSignatureData,
        employeeSignature: employeeSignatureData,
        allItems: isCheckIn ? allItems : damageItems,
        photos: allPhotos,
        timestamp: new Date().toISOString()
      };
      if (checkInDur) Object.assign(page5AdditionalData, checkInDur);

      // ✅ SAVE ALL DATA TO API BEFORE PDF/EMAIL
      if (currentBookingNumber) {
        try {
          // Load existing booking to get all page data
          const existingBooking = await getBooking(currentBookingNumber);

          // Prepare complete booking update
          const completeUpdate = {
            bookingData: bookingData,
            page2DataCheckIn: existingBooking?.page2DataCheckIn || null,
            page2DataCheckOut: existingBooking?.page2DataCheckOut || null,
            page3DataCheckIn: existingBooking?.page3DataCheckIn || null,
            page3DataCheckOut: existingBooking?.page3DataCheckOut || null,
            page4DataCheckIn: existingBooking?.page4DataCheckIn || null,
            page4DataCheckOut: existingBooking?.page4DataCheckOut || null
          };

          // Add page5 data to the appropriate mode
          if (mode === 'in') {
            completeUpdate.page5DataCheckIn = page5AdditionalData;
          } else {
            completeUpdate.page5DataCheckOut = page5AdditionalData;
          }

          // Save complete booking to API
          await saveBooking(currentBookingNumber, completeUpdate);
          console.log('✅ Complete booking saved to API');

          // 🔥 NEW (01May2026): Mark check-in/check-out as SUBMITTED in page5_agreement
          // This is the source-of-truth flag that Page 1 reads to unlock check-out mode.
          //
          // 🔒 SNAPSHOT (Λύση Β / ΕΠΙΛΟΓΗ 3 — v6.175): freeze Page 4 list at submit.
          // Captures item_key + 10-lang labels + sort_order + price + qty + inOk + out + media,
          // so submitted bookings render from a frozen artifact regardless of later
          // yacht_items / master_items changes (legal: signed documents must not drift).
          // Re-submit policy = freshest wins (overwrite) — see api/page5-agreement.php.
          let itemsSnapshot: any = null;
          try {
            // Helper: build item_key → {sort_order, labels} lookup from a master_items array.
            const buildByKey = (masterItems: any[]) => {
              const map: Record<string, any> = {};
              for (const mi of (masterItems || [])) {
                map[mi.item_key] = {
                  sort_order: mi.sort_order ?? null,
                  labels: {
                    el: mi.label_el, en: mi.label_en, it: mi.label_it, de: mi.label_de,
                    ru: mi.label_ru, fr: mi.label_fr, ro: mi.label_ro, pl: mi.label_pl,
                    he: mi.label_he, es: mi.label_es
                  }
                };
              }
              return map;
            };

            // Helper: shape a saved item to the canonical snapshot row.
            const shapeItem = (it: any, byKey: Record<string, any>) => ({
              item_key: it.key,
              labels: byKey[it.key]?.labels || null,
              sort_order: byKey[it.key]?.sort_order ?? null,
              // check-out override (χειροκίνητη τιμή ζημιάς) υπερισχύει· check-in αμετάβλητο
              price: (mode === 'out' && priceOverrides[it.key] != null && priceOverrides[it.key] !== '')
                ? String(priceOverrides[it.key]).replace(',', '.') : (it.price ?? ""),
              qty: it.qty ?? 1,
              inOk: !!it.inOk,
              out: it.out ?? null,
              media: it.media || []
            });

            // Parallel fetch: saved data + master_items + master_sections για page2+page3+page4.
            // Κάθε individual catch fallback → δεν μπορεί να reject το Promise.all.
            const [
              p2Data, p3Data, p4Data,
              masterP2, masterP3, masterP4,
              secP2, secP3
            ] = await Promise.all([
              getPage2DataHybrid(currentBookingNumber, mode).catch(() => null),
              getPage3DataHybrid(currentBookingNumber, mode).catch(() => null),
              getPage4DataHybrid(currentBookingNumber, mode).catch(() => null),
              getMasterItems(undefined, 'page2').catch(() => []),
              getMasterItems(undefined, 'page3').catch(() => []),
              getMasterItems(undefined, 'page4').catch(() => []),
              getMasterSections('page2').catch(() => []),
              getMasterSections('page3').catch(() => []),
            ]);

            const byKeyP2 = buildByKey(masterP2);
            const byKeyP3 = buildByKey(masterP3);
            const byKeyP4 = buildByKey(masterP4);

            // Per-page try/catch — partial failure ΔΕΝ ρίχνει ολόκληρο το snapshot.
            // Key στο output είναι section_key (forward-compat με δυναμικά sections όπως cabin),
            // αλλά το lookup στα saved data γίνεται μέσω legacy_key (page-scoped).
            let page2Out: Record<string, any[]> = {};
            let page3Out: Record<string, any[]> = {};
            let page4Out: Record<string, any[]> = {};

            try {
              for (const sec of (secP2 || [])) {
                if (sec.active === false) continue;
                const savedKey = sec.legacy_key || sec.section_key;
                const raw = (p2Data && (p2Data as any)[savedKey]) || [];
                const arr = Array.isArray(raw) ? raw : [];
                page2Out[sec.section_key] = arr.map((it: any) => shapeItem(it, byKeyP2));
              }
            } catch (e2) {
              console.error('⚠️ snapshot page2 build failed (non-fatal):', e2);
              runtimeWarnings.push('Το αποτύπωμα (snapshot) της Σελίδας 2 ΔΕΝ αποθηκεύτηκε.');
              page2Out = {};
            }

            try {
              for (const sec of (secP3 || [])) {
                if (sec.active === false) continue;
                const savedKey = sec.legacy_key || sec.section_key;
                const raw = (p3Data && (p3Data as any)[savedKey]) || [];
                const arr = Array.isArray(raw) ? raw : [];
                page3Out[sec.section_key] = arr.map((it: any) => shapeItem(it, byKeyP3));
              }
            } catch (e3) {
              console.error('⚠️ snapshot page3 build failed (non-fatal):', e3);
              runtimeWarnings.push('Το αποτύπωμα (snapshot) της Σελίδας 3 ΔΕΝ αποθηκεύτηκε.');
              page3Out = {};
            }

            try {
              const tcSource = (p4Data && (p4Data as any).technicalCheckItems) || [];
              if (Array.isArray(tcSource) && tcSource.length > 0) {
                page4Out.technical_check = tcSource.map((it: any) => shapeItem(it, byKeyP4));
              } else {
                // p4 fetch failed / empty: do NOT write an empty technical_check (it would defeat
                // the page4 freeze and blank the legal snapshot). Preserve a previously captured
                // page4 snapshot if one exists; otherwise omit the key. Submit stays non-blocking.
                try {
                  const prevAg = await getPage5Agreement(currentBookingNumber);
                  const prevRow = mode === 'in' ? prevAg?.checkIn : prevAg?.checkOut;
                  const prevP4 = prevRow?.items_snapshot?.page4;
                  const prevTC = prevP4?.technical_check;
                  if (Array.isArray(prevTC) && prevTC.length > 0) {
                    page4Out = prevP4;
                    console.warn('snapshot page4: live empty - preserved previous page4 snapshot (' + prevTC.length + ' items)');
                  } else {
                    console.warn('snapshot page4: no live items and no previous snapshot - omitting page4 key (non-fatal)');
                    runtimeWarnings.push('Η Σελίδα 4 δεν είχε στοιχεία — το αποτύπωμα σελ.4 ΔΕΝ γράφτηκε.');
                  }
                } catch (mergeErr) {
                  console.warn('snapshot page4: preserve-previous fetch failed - omitting page4 key (non-fatal):', mergeErr);
                  runtimeWarnings.push('Αποτυχία ανάκτησης προηγούμενου αποτυπώματος Σελίδας 4.');
                }
              }
            } catch (e4) {
              console.error('⚠️ snapshot page4 build failed (non-fatal):', e4);
              runtimeWarnings.push('Το αποτύπωμα (snapshot) της Σελίδας 4 ΔΕΝ αποθηκεύτηκε.');
              page4Out = {};
            }

            itemsSnapshot = {
              schema_version: 2,
              captured_at: new Date().toISOString(),
              page2: page2Out,
              page3: page3Out,
              page4: page4Out
            };

            const p2Total = Object.values(page2Out).reduce((s, arr) => s + arr.length, 0);
            const p3Total = Object.values(page3Out).reduce((s, arr) => s + arr.length, 0);
            console.log('🔒 items_snapshot built:',
              'page2 sections =', Object.keys(page2Out).length, '(items', p2Total + ')',
              '| page3 sections =', Object.keys(page3Out).length, '(items', p3Total + ')',
              '| page4.technical_check =', (page4Out.technical_check || []).length);
          } catch (snapErr) {
            console.error('⚠️ Failed to build items_snapshot (non-fatal, will save without):', snapErr);
            runtimeWarnings.push('ΔΕΝ δημιουργήθηκε αποτύπωμα ειδών (items_snapshot) — τα PDF/έγγραφα μπορεί να είναι ελλιπή.');
            itemsSnapshot = null;
          }

          if (itemsSnapshot && typeof itemsSnapshot === 'object' && checkInDur) {
            itemsSnapshot.checkInDuration = { startedAt: checkInDur.checkInStartedAt, completedAt: checkInDur.checkInCompletedAt, durationMinutes: checkInDur.checkInDurationMinutes };
          }
          try {
            await savePage5Agreement(currentBookingNumber, mode, {
              terms_accepted: !!termsAccepted,
              skipper_signature: skipperSignatureData || null,
              company_signature: employeeSignatureData || null,
              company_representative: null,
              email_sent: false, // will be set to true after email succeeds (below)
              email_sent_at: null,
              notes: notes || null,
              items_snapshot: itemsSnapshot,
            });
            console.log('✅ page5_agreement record saved (mode=' + mode + ')');
          } catch (p5err: any) {
            // ⛔ ΚΡΙΣΙΜΟ — χωρίς page5_agreement δεν υπάρχει υποβολή. ΔΕΝ συνεχίζουμε σιωπηλά.
            console.error('❌ Failed to save page5_agreement:', p5err);
            failSubmit(
              (lang === 'el' ? 'Η αποθήκευση της υποβολής στον server απέτυχε: ' : 'Saving the submission on the server failed: ')
                + (p5err?.message || p5err),
              runtimeWarnings, p5err);
            return;
          }

          // ⚠️ ΔΕΝ μπαίνει εδώ setIsSubmitted(true). Το πράσινο «Sent» μπαίνει ΜΟΝΟ
          // αφού ο server απαντήσει 200 στο email ΚΑΙ γραφτεί email_sent=true.
        } catch (error: any) {
          console.error('⚠️ Failed to save complete booking to API:', error);
          runtimeWarnings.push((lang === 'el'
            ? 'Η πλήρης αποθήκευση του ναύλου στο API απέτυχε: '
            : 'Full booking save to the API failed: ') + (error?.message || error));
        }
      }

      await loadPdfFonts(); // εξασφαλιζει DejaVuSans (Unicode) πριν το PDF render
      const pdfDoc = generateLuxuryPDF(bookingData, mode, page5AdditionalData, lang, { isPage5: true });
      if (!pdfDoc) {
        // Το PDF είναι ΤΟΠΙΚΟ (jsPDF) — αποτυχία εδώ ΔΕΝ είναι δικτυακή, δεν μπαίνει σε ουρά.
        setIsSubmitting(false);
        setIsSubmitted(false);
        setSubmitFailed(true);
        setAwaitingNetwork(false);
        setSubmitBlock({
          kind: 'failed', blockers: [], warnings: runtimeWarnings,
          error: lang === 'el' ? 'Αποτυχία δημιουργίας PDF.' : 'PDF generation failed.',
        });
        return;
      }

      const pdfBlob = pdfDoc.output('blob');
      console.log('PDF Blob size:', pdfBlob?.size || 0);
      const emailResult = await sendEmailWithPDF(bookingData, pdfBlob, mode, lang, page5AdditionalData);

      // ⚠️ v6.478 — κάθε warning του emailService γίνεται ΟΡΑΤΟ.
      if (emailResult && Array.isArray((emailResult as any).warnings)) {
        runtimeWarnings.push(...(emailResult as any).warnings);
      }

      if (emailResult.success) {
        // 🔧 FIX (Σύμπτωμα 2): γράψε το email_sent flag στο page5_agreement μετά από επιτυχές send.
        //    Δεν στέλνουμε ξανά υπογραφές/snapshot — μόνο τα δύο πεδία (το backend δεν αγγίζει
        //    items_snapshot αν δεν παρασχεθεί, και τις υπογραφές με EXCLUDED τις ξαναγράφει
        //    όπως ήταν στο τελευταίο call, οπότε περνάμε ξανά τις ίδιες τιμές για να μην χαθούν).
        try {
          await savePage5Agreement(currentBookingNumber, mode, {
            terms_accepted: !!termsAccepted,
            skipper_signature: page5AdditionalData?.skipperSignature || null,
            company_signature: page5AdditionalData?.employeeSignature || null,
            company_representative: null,
            email_sent: true,
            email_sent_at: new Date().toISOString(),
            notes: notes || null,
          });
          console.log('📧 page5_agreement: email_sent=true persisted');
        } catch (flagErr: any) {
          // ⛔ ΚΡΙΣΙΜΟ — χωρίς email_sent=true η υποβολή ΔΕΝ θεωρείται ολοκληρωμένη.
          console.error('❌ Failed to persist email_sent flag:', flagErr);
          failSubmit(
            (lang === 'el'
              ? 'Το email στάλθηκε αλλά ΔΕΝ καταγράφηκε στη βάση (email_sent). Η υποβολή ΔΕΝ θεωρείται ολοκληρωμένη: '
              : 'Email sent but NOT recorded in the database (email_sent). The submission is NOT complete: ')
              + (flagErr?.message || flagErr),
            runtimeWarnings, flagErr);
          return;
        }

        // 🌉 Επιτυχές submit → η υπογραφή ζει πλέον ΜΟΝΟ στον server.
        // Σβήνουμε την τοπική γέφυρα από τη συσκευή.
        try { localStorage.removeItem(`page5_skipper_signature_${currentBookingNumber}_${mode}`); } catch (e) {}

        // ✅ ΜΟΝΟ ΕΔΩ: server 200 στο email ΚΑΙ email_sent=true γραμμένο στη βάση.
        setAwaitingNetwork(false);   // η ουρά άδειασε
        setRetryAttempt(0);
        setIsSubmitted(true);
        setIsSubmitting(false);
        setSubmitFailed(false);
        setSubmitBlock({ kind: 'sent', blockers: [], warnings: runtimeWarnings });
        window.dispatchEvent(new CustomEvent('pendingCheckinsChanged'));
      } else {
        const emailErr = ((emailResult as any)?.results?.[0]?.error) || (emailResult as any)?.error || 'unknown';
        failSubmit(
          (lang === 'el' ? 'Η αποστολή email απέτυχε: ' : 'Email sending failed: ') + emailErr,
          runtimeWarnings, emailErr);
      }
    } catch (error: any) {
      console.error('❌ Submit error:', error);
      failSubmit(
        (lang === 'el' ? 'Σφάλμα υποβολής: ' : 'Submit error: ') + (error?.message || error),
        runtimeWarnings, error);
    }
  };

  // Κράτα ΠΑΝΤΑ την τελευταία έκδοση της handleSubmit, ώστε το auto-retry να
  // τρέχει με ΦΡΕΣΚΟ state (όχι stale closure από τη στιγμή της αποτυχίας).
  handleSubmitRef.current = handleSubmit;

  // 🔄 AUTO-RETRY: μόλις επανέλθει το δίκτυο, ξαναδοκιμάζει ΜΟΝΟ ΤΟΥ.
  useEffect(() => {
    if (!awaitingNetwork) return;

    const fire = async (why: string) => {
      if (retryRunningRef.current) return;
      if (retryAttempt >= MAX_AUTO_RETRIES) {
        setAwaitingNetwork(false);
        setSubmitBlock({
          kind: 'failed', blockers: [], warnings: [],
          error: lang === 'el'
            ? ('Δοκίμασα ' + MAX_AUTO_RETRIES + ' φορές χωρίς επιτυχία. Πάτα «Δοκίμασε ξανά».')
            : ('Tried ' + MAX_AUTO_RETRIES + ' times without success. Press "Try again".'),
        });
        return;
      }
      retryRunningRef.current = true;
      setRetryAttempt((n) => n + 1);
      setAwaitingNetwork(false);
      console.log('🔄 auto-retry submit (' + why + '), attempt', retryAttempt + 1);
      try {
        await handleSubmitRef.current?.({ isRetry: true });
      } finally {
        retryRunningRef.current = false;
      }
    };

    const onOnline = () => fire('online event');
    window.addEventListener('online', onOnline);

    // Το «online» ΔΕΝ πυροδοτείται όταν το wifi είναι όρθιο αλλά ο server ήταν
    // άφταστος (captive portal, πεσμένο backend). Γι' αυτό και περιοδικός έλεγχος.
    const tick = setInterval(() => {
      if (typeof navigator === 'undefined' || navigator.onLine) fire('periodic check');
    }, 30000);

    return () => {
      window.removeEventListener('online', onOnline);
      clearInterval(tick);
    };
  }, [awaitingNetwork, retryAttempt, lang]);
  
  const handlePrevious = () => {
    if (onNavigate && typeof onNavigate === 'function') {
      onNavigate('prev');
    }
  };

// =================================================================
// END OF PART 3 - Continue with PART 4 (JSX RENDER)
// =================================================================
// =================================================================
// =================================================================
// PAGE 5 - FINAL CORRECT VERSION - PART 4 OF 4 (FINAL!)
// =================================================================
// JSX RENDER - COMPLETE & CORRECT
// =================================================================
// ✅ Single Skipper Signature
// ✅ Payment Auth WITHOUT signature box
// ✅ All syntax correct
// PASTE THIS AFTER PART 3 - CONTINUING THE Page5 FUNCTION
// =================================================================

  // JSX RENDER STARTS HERE
  return (
    <div className="min-h-screen p-6" style={{ background: brand.pageBg }}>
      <div className="max-w-6xl mx-auto bg-white shadow rounded-2xl p-6" style={{ border: `1px solid ${brand.black}` }}>
        <div className="mb-4">
          <button
            onClick={() => navigatePage5('/home')}
            className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-blue-50 transition-all"
            title={lang === 'el' ? 'Αρχικη' : 'Home'}
            style={{ border: `1px solid ${brand.blue}`, width: '72px' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={brand.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span className="text-xs mt-1 font-medium" style={{ color: brand.blue }}>{lang === 'el' ? 'Αρχικη' : 'Home'}</span>
          </button>
        </div>
        {SHOW_BOOKING_INFO_ON_CHECKLIST_PAGES && (
        <BookingInfoBox 
          bookingInfo={{
            bookingNumber: bookingData.bookingNumber || localStorage.getItem('currentBooking') || "N/A",
            vesselName: bookingData.vesselName || bookingData.selectedVessel || "N/A",
            skipperFirstName: bookingData.skipperFirstName || bookingData.bookerName || "N/A",
            skipperLastName: bookingData.skipperLastName || "",
            checkInDate: bookingData.checkInDate || "N/A",
            checkOutDate: bookingData.checkOutDate || "N/A"
          }}
          currentBookingNumber={bookingData.bookingNumber || localStorage.getItem('currentBooking') || "N/A"}
        />
        )}
        
        <div className="flex justify-center mb-6">
          <div className="h-16 px-6 rounded flex items-center justify-center font-bold text-xl" style={{ backgroundColor: brand.blue, color: 'white' }}>
            TAILWIND YACHTING
          </div>
        </div>
        
        <PageHeader title={t.pageTitle} />
        
        <div className="mb-4">
          <div className="text-base font-semibold" style={{ color: brand.black }}>
            Mode: <span style={{ color: mode === "in" ? brand.blue : brand.pink, fontSize: '17px' }}>
              {mode === "in" ? "Check-in" : "Check-out"}
            </span>
          </div>
        </div>
        
        <TopControls
          isOnline={isOnline}
          lang={lang}
          setLang={setLang}
          isEmployee={isEmployee}
          currentEmployee={currentEmployee}
          onEmployeeLogout={handleEmployeeLogout}
          onEmployeeLogin={handleEmployeeLogin}
          percentage={percentage}
        />

        {/* 🔒 EMPLOYEE LOGIN BOX */}
        <div 
          ref={employeeLoginRef}
          className="mb-6 p-6 rounded-xl border-3"
          style={{
            backgroundColor: isEmployee ? brand.successBg : '#fff3cd',
            borderColor: isEmployee ? brand.successBorder : '#ffc107'
          }}
        >
          {!isEmployee ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <span style={{ fontSize: '32px' }}>🔒</span>
                <h3 className="text-2xl font-bold" style={{ color: '#856404' }}>
                  {t.employeeCodeRequired}
                </h3>
              </div>
              
              <p className="mb-4 text-base" style={{ color: '#856404' }}>
                {lang === 'el' 
                  ? 'Απαιτείται κωδικός υπαλλήλου για να ξεκλειδώσετε την υπογραφή, το PDF και το Submit.'
                  : 'Employee code required to unlock signature, PDF, and Submit.'}
              </p>

              <button
                onClick={handleEmployeeLogin}
                className="w-full px-6 py-4 rounded-xl font-bold text-white text-lg hover:scale-105 transition-all"
                style={{ backgroundColor: brand.blue }}
              >
                🔐 {lang === 'el' ? 'Σύνδεση Υπαλλήλου' : 'Employee Login'}
              </button>
            </>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span style={{ fontSize: '48px' }}>✅</span>
                <div>
                  <h3 className="text-2xl font-bold" style={{ color: brand.successText }}>
                    {currentEmployee.name}
                  </h3>
                  <p className="text-base" style={{ color: brand.successText }}>
                    {t.loggedIn || 'Logged in'}
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleEmployeeLogout}
                className="px-6 py-3 rounded-xl font-bold border-2 hover:bg-white transition-all"
                style={{ 
                  borderColor: brand.successBorder, 
                  color: brand.successText,
                  backgroundColor: 'transparent'
                }}
              >
                {t.logoutBtn || 'Logout'}
              </button>
            </div>
          )}
        </div>
        
        {/* AGREEMENTS — boxes moved to Page 3 (DB page_group=page3). Consent loaded from box-consents.php (consent loader useEffect). Empty ref divs kept so submit scrollIntoView still resolves. */}
        <div ref={returnRef} />
        <div ref={termsRef} />
        <div ref={privacyRef} />
        <div ref={warningRef} />
        
        {/* READ-ONLY consent summary (config-aware). Renders only visible boxes for the mode; null in check-out (all hidden). */}
        <ConsentSummary currentBookingNumber={currentBookingNumber} mode={mode} lang={lang} t={t} />

        {/* CHECK-IN: Complete Inventory */}
        {isCheckIn && <CompleteInventory allItems={allItems} t={t} mode={mode} />}
        
        {/* CHECK-OUT: Damage Inventory */}
        {isCheckOut && <DamageInventory items={damageItems} t={t} lang={lang}
          overrides={priceOverrides}
          onPriceChange={(key: string, val: string) => setPriceOverrides(prev => ({ ...prev, [key]: val }))} />}
        
        {/* PHOTOS */}
        {Object.keys(allPhotos).length > 0 && (
          <div className="mb-6 border-2 rounded-xl p-4" style={{ borderColor: brand.blue, background: "#f0f9ff" }}>
            <h3 className="font-bold text-xl mb-4 text-center" style={{ color: brand.navy }}>
              {isCheckOut 
                ? (t.damagePhotosBtn || '📸 DAMAGE PHOTOS')
                : (t.photosBtn || '📸 PHOTOS')
              }
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {Object.entries(allPhotos).map(([itemKey, photos]) => {
                const photoArray = Array.isArray(photos) ? photos : [photos];
                return photoArray.map((photo, idx) => {
                  if (!photo) return null;
                  return (
                    <div key={`${itemKey}-${idx}`} className="border rounded-lg p-1 bg-white overflow-hidden" style={{ borderColor: brand.blue }}>
                      <img 
                        src={photo} 
                        alt={`${itemKey} ${idx + 1}`} 
                        className="w-full h-24 object-cover rounded"
                      />
                      <p className="text-xs mt-1 text-center font-semibold truncate" style={{ color: brand.grey }}>
                        {itemKey}
                      </p>
                    </div>
                  );
                });
              })}
            </div>
          </div>
        )}
        
        {/* NOTES */}
        <div className="mb-6 border-2 rounded-xl p-4" style={{ borderColor: brand.blue }}>
          <label className="block font-semibold mb-2" style={{ color: brand.black }}>
            {t.notesTitle}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border rounded p-3 min-h-[180px] bg-white"
            placeholder={t.notesPlaceholder}
            style={{ borderColor: brand.black, color: brand.black }}
          />
        </div>

        {/* ✅ PAYMENT AUTHORIZATION (CHECK-OUT ONLY) - BEFORE SIGNATURES! */}
        {isCheckOut && (
          <div ref={paymentAuthRef} className="mt-6">
            <div 
              className="border-2 rounded-xl p-6 transition-all duration-500"
              style={{
                borderColor: highlightPaymentAuth ? brand.errorBorder : (paymentAuthAccepted ? brand.successBorder : brand.blue),
                background: highlightPaymentAuth ? brand.errorBg : (paymentAuthAccepted ? brand.successBg : '#f0f9ff'),
                opacity: noDamageReleaseAccepted ? 0.6 : 1
              }}
            >
              <h3 className="font-bold text-xl mb-4" style={{ color: brand.navy }}>
                {t.paymentAuthTitle} *
              </h3>
              <p className="text-base mb-4" style={{ color: brand.black }}>
                {t.paymentAuthText}
              </p>
              
              {/* ✅ ONLY CHECKBOX - NO SIGNATURE BOX HERE! */}
              <label className="flex items-center gap-3 cursor-pointer p-4 bg-white rounded-lg border-2 hover:bg-gray-50 transition-colors"
                     style={{ borderColor: paymentAuthAccepted ? brand.successBorder : brand.blue }}>
                <input
                  type="checkbox"
                  checked={paymentAuthAccepted}
                  disabled={noDamageReleaseAccepted}
                  onChange={() => {
                    const next = !paymentAuthAccepted;
                    setPaymentAuthAccepted(next);
                    if (next) setNoDamageReleaseAccepted(false);
                  }}
                  className="w-5 h-5 cursor-pointer"
                />
                <span className="font-semibold" style={{ color: brand.black }}>
                  {t.paymentAuthAccept}
                </span>
              </label>
              
              {/* ✅ NOTE: The skipper signature below covers this authorization */}
              <p className="text-sm mt-3 text-center italic" style={{ color: brand.grey }}>
                {lang === 'el' 
                  ? '* Η υπογραφή του κυβερνήτη παρακάτω καλύπτει και αυτήν την εξουσιοδότηση'
                  : '* The skipper signature below covers this authorization'}
              </p>
            </div>
          </div>
        )}

        {/* ✅ CARD RELEASE — NO DAMAGE (CHECK-OUT ONLY). Mutually exclusive with Payment Authorization. */}
        {isCheckOut && (
          <div className="mt-6">
            <div
              className="border-2 rounded-xl p-6 transition-all duration-500"
              style={{
                borderColor: noDamageReleaseAccepted ? brand.successBorder : brand.blue,
                background: noDamageReleaseAccepted ? brand.successBg : '#f0f9ff',
                opacity: paymentAuthAccepted ? 0.6 : 1
              }}
            >
              <h3 className="font-bold text-xl mb-4" style={{ color: brand.navy }}>
                {t.cardReleaseTitle} *
              </h3>
              <p className="text-base mb-4" style={{ color: brand.black }}>
                {t.cardReleaseText}
              </p>
              <label className="flex items-center gap-3 cursor-pointer p-4 bg-white rounded-lg border-2 hover:bg-gray-50 transition-colors"
                     style={{ borderColor: noDamageReleaseAccepted ? brand.successBorder : brand.blue }}>
                <input
                  type="checkbox"
                  checked={noDamageReleaseAccepted}
                  disabled={paymentAuthAccepted}
                  onChange={() => {
                    const next = !noDamageReleaseAccepted;
                    setNoDamageReleaseAccepted(next);
                    if (next) setPaymentAuthAccepted(false);
                  }}
                  className="w-5 h-5 cursor-pointer"
                />
                <span className="font-semibold" style={{ color: brand.black }}>
                  {t.cardReleaseAccept}
                </span>
              </label>
              <p className="text-sm mt-3 text-center italic" style={{ color: brand.grey }}>
                {lang === 'el'
                  ? '* Η υπογραφή του κυβερνήτη παρακάτω καλύπτει και αυτήν την αποδέσμευση'
                  : '* The skipper signature below covers this release'}
              </p>
            </div>
          </div>
        )}

        {/* ✅ SINGLE SKIPPER SIGNATURE (THE ONLY ONE!) */}
        <div ref={skipperSignatureRef}>
          <SignatureBox 
            brand={brand}
            lang={lang}
            onSignChange={setSkipperSigned}
            onImageChange={(img) => {
              setSignatureImage(img);
              // 🌉 v6.478 — ΠΡΟΣΩΡΙΝΗ ΓΕΦΥΡΑ ΓΙΑ ΠΤΩΣΗ ΔΙΚΤΥΟΥ.
              // Κλειδί ανά ΝΑΥΛΟ + ανά MODE, ώστε το check-out να ΜΗΝ κληρονομεί
              // ποτέ την υπογραφή του check-in. Σβήνεται μετά από επιτυχές submit.
              try {
                if (currentBookingNumber) {
                  const sigKey = `page5_skipper_signature_${currentBookingNumber}_${mode}`;
                  if (img && img.length > 100) localStorage.setItem(sigKey, img);
                  else localStorage.removeItem(sigKey);   // «Καθαρισμός» → φεύγει
                }
              } catch (e) { /* quota / private mode */ }
              const timer = setTimeout(() => {
                const storageKey = mode === 'in' ? `page5DataCheckIn_${currentBookingNumber}` : `page5DataCheckOut_${currentBookingNumber}`;
                const existingData = JSON.parse(localStorage.getItem(storageKey) || '{}');
                existingData.skipperSignatureData = img;
                existingData.skipperSignature = !!img;
                localStorage.setItem(storageKey, JSON.stringify(existingData));
              }, 500);
              return () => clearTimeout(timer);
            }}
            initialImage={signatureImage}
            currentBookingNumber={currentBookingNumber}
            mode={mode}
            pageNumber={5}
            title={t.skipperSignatureTitle}
            highlightError={highlightSkipperSignature}
          />
        </div>
        
        {/* 🔒 EMPLOYEE SIGNATURE WITH LOCK */}
        <div ref={employeeSignatureRef}>
          <EmployeeSignatureWithLogin 
            brand={brand}
            t={{...t, lang}}
            signed={employeeSigned}
            setSigned={setEmployeeSigned}
            canvasRef={employeeCanvasRef}
            onImageChange={(img) => {
              setEmployeeSignatureImage(img);
              const timer = setTimeout(() => {
                const storageKey = mode === 'in' ? `page5DataCheckIn_${currentBookingNumber}` : `page5DataCheckOut_${currentBookingNumber}`;
                const existingData = JSON.parse(localStorage.getItem(storageKey) || '{}');
                existingData.employeeSignatureData = img;
                existingData.employeeSignature = !!img;
                localStorage.setItem(storageKey, JSON.stringify(existingData));
              }, 500);
              return () => clearTimeout(timer);
            }}
            highlightError={highlightEmployeeSignature}
            title={t.employeeSignatureTitle}
            isEmployee={isEmployee}
            currentEmployee={currentEmployee}
            onLoginClick={() => {
              handleEmployeeLogin();
              setTimeout(() => {
                employeeLoginRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 100);
            }}
          />
        </div>
        
        {/* ACTION BUTTONS */}
        <div className="mt-6 grid grid-cols-2 md:flex md:flex-wrap md:justify-between gap-2 md:gap-3">
          <button 
            type="button" 
            onClick={handlePrevious} 
            className="px-4 py-3 rounded font-semibold bg-gray-500 text-white hover:bg-gray-600 transition-colors min-h-[44px] col-span-2 md:col-span-1"
          >
            ← {t.back}
          </button>
          <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-3 col-span-2 md:col-span-1">
            <button 
              type="button" 
              onClick={handleSaveDraft} 
              className="px-4 py-3 rounded transition-colors bg-gray-500 text-white hover:bg-gray-600 min-h-[44px]"
            >
              {t.save}
            </button>
            {/* 🔒 PDF BUTTON WITH LOCK */}
            <button 
              type="button" 
              onClick={handleGeneratePDF} 
              disabled={areButtonsLocked || isSubmitting || windowLocked || (isCheckIn && isSubmitted)}
              className="px-4 py-2 rounded font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 min-h-[44px]" 
              style={{ background: areButtonsLocked ? '#9ca3af' : '#dc2626', color: '#fff' }}
              title={areButtonsLocked ? (lang === 'el' ? 'Χρειάζεται κωδικός υπαλλήλου' : 'Employee code required') : ''}
            >
              {areButtonsLocked ? '🔒' : '📄'} {t.pdf}
            </button>
            {/* 🔒 SUBMIT BUTTON WITH LOCK */}
            <button 
              type="button" 
              onClick={() => handleSubmit()} 
              disabled={areButtonsLocked || isSubmitting || windowLocked || (isCheckIn && isSubmitted)}
              className="px-4 py-3 rounded font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 min-h-[44px]"
              style={{ background: isSubmitted ? '#22c55e' : submitFailed ? '#dc2626' : (areButtonsLocked || isSubmitting) ? '#9ca3af' : brand.blue, color: '#fff' }}
              title={areButtonsLocked ? (lang === 'el' ? 'Χρειάζεται κωδικός υπαλλήλου' : 'Employee code required') : ''}
            >
                {/* ✅ Πράσινο «Sent» ΜΟΝΟ μετά από 200 + email_sent=true στη βάση. */}
                {isSubmitted
                  ? '✅ Sent!'
                  : isSubmitting
                    ? '⏳...'
                    : submitFailed
                      ? (lang === 'el' ? '🔁 Δοκίμασε ξανά' : '🔁 Try again')
                      : areButtonsLocked ? '🔒' : ('✓ ' + t.submit)}
            </button>
          </div>
        </div>
        
        {/* FOOTER */}
        <div className="mt-8 pt-6 border-t text-center text-sm" style={{ color: '#6b7280' }}>
          <div>{t.footerAddress}</div>
          <div className="mt-1">{t.footerWebsite}</div>
          <div className="mt-1">{t.footerPhone}</div>
          <div className="mt-1">
            info@tailwindyachting.com | charter@tailwindyachting.com | accounting@tailwindyachting.com
          </div>
        </div>
      </div>

      {/* EMPLOYEE LOGIN MODAL */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowLoginModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">🔒</span>
              <h3 className="text-xl font-bold" style={{ color: brand.black }}>
                {lang === 'el' ? 'Σύνδεση Υπαλλήλου' : 'Employee Login'}
              </h3>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2" style={{ color: brand.black }}>
                {lang === 'el' ? 'Κωδικός Υπαλλήλου:' : 'Employee Code:'}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="employee-code-input"
                  type={showEmployeePassword ? "text" : "password"}
                  placeholder={lang === 'el' ? 'Εισάγετε κωδικό υπαλλήλου' : 'Enter employee code'}
                  autoFocus
                  className="w-full px-3 py-2 border-2 rounded"
                  style={{ borderColor: brand.black, paddingRight: '40px' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const code = e.target.value.trim();
                      if (!code) {
                        alert(lang === 'el' ? 'Παρακαλώ εισάγετε κωδικό' : 'Please enter a code');
                        return;
                      }
                      const user = authService.login(code);
                      if (user) {
                        setIsEmployee(true);
                        setCurrentEmployee(user.permissions);
                        setShowLoginModal(false);
                        sessionStorage.setItem('currentEmployee', JSON.stringify(user));
                      } else {
                        alert(lang === 'el' ? 'Λάθος κωδικός' : 'Invalid code');
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowEmployeePassword(!showEmployeePassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '20px',
                    color: brand.black
                  }}
                >
                  {showEmployeePassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setShowLoginModal(false)} 
                className="px-6 py-2 rounded border hover:bg-gray-50 transition-colors" 
                style={{ borderColor: brand.black, color: brand.black }}
              >
                {t.cancelBtn}
              </button>
              <button
                onClick={() => {
                  const input = document.getElementById('employee-code-input');
                  const code = input?.value.trim();
                  if (!code) {
                    alert(lang === 'el' ? 'Παρακαλώ εισάγετε κωδικό' : 'Please enter a code');
                    return;
                  }
                  const user = authService.login(code);
                  if (user) {
                    setIsEmployee(true);
                    setCurrentEmployee(user.permissions);
                    setShowLoginModal(false);
                    sessionStorage.setItem('currentEmployee', JSON.stringify(user));
                  } else {
                    alert(lang === 'el' ? 'Λάθος κωδικός' : 'Invalid code');
                    if (input) input.value = '';
                  }
                }}
                className="px-6 py-2 rounded text-white hover:bg-blue-600 transition-colors font-semibold" 
                style={{ background: brand.blue }}
              >
                {lang === 'el' ? 'Σύνδεση' : 'Login'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔄 v6.479 — ΜΠΑΝΕΡ ΟΥΡΑΣ. Ορατό ΟΣΟ εκκρεμεί η επαναποστολή, ακόμη κι */}
      {/* αν κλείσει το modal. Κάτω μέρος οθόνης, δεν συγκρούεται με τη μπάρα.   */}
      {awaitingNetwork && (
        <div
          style={{
            position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 19999,
            background: '#b45309', color: '#fff', padding: '12px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, fontWeight: 800, fontSize: 15,
            boxShadow: '0 -2px 12px rgba(0,0,0,0.35)',
          }}
        >
          <span>🔄</span>
          <span>
            {lang === 'el'
              ? 'ΔΕΝ ΥΠΟΒΛΗΘΗΚΕ — Θα ξαναδοκιμάσω μόλις επανέλθει το δίκτυο'
              : 'NOT SUBMITTED — I will retry as soon as the network is back'}
          </span>
          {retryAttempt > 0 && (
            <span style={{ fontWeight: 600, fontSize: 13, opacity: 0.9 }}>
              ({lang === 'el' ? 'προσπάθεια' : 'attempt'} {retryAttempt}/{MAX_AUTO_RETRIES})
            </span>
          )}
        </div>
      )}

      {/* ===================================================================== */}
      {/* 🚨 v6.478 — MODAL ΥΠΟΒΟΛΗΣ. ΔΕΝ κλείνει μόνο του, δεν έχει timer,      */}
      {/* δεν κλείνει με κλικ έξω. Κλείνει ΜΟΝΟ με το κουμπί του.                */}
      {/* ===================================================================== */}
      {submitBlock && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
            zIndex: 20000, display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: 16,
          }}
        >
          <div
            style={{
              background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%',
              maxHeight: '86vh', overflowY: 'auto',
              border: '4px solid ' + (submitBlock.kind === 'sent' ? '#16a34a' : '#dc2626'),
            }}
          >
            <div
              style={{
                background: submitBlock.kind === 'sent' ? '#16a34a' : '#dc2626',
                color: '#fff', padding: '16px 20px', fontWeight: 900, fontSize: 20,
              }}
            >
              {submitBlock.kind === 'sent'
                ? (lang === 'el' ? '✅ ΥΠΟΒΛΗΘΗΚΕ' : '✅ SUBMITTED')
                : (lang === 'el' ? '⛔ ΔΕΝ ΥΠΟΒΛΗΘΗΚΕ' : '⛔ NOT SUBMITTED')}
            </div>

            <div style={{ padding: '16px 20px' }}>
              {submitBlock.kind === 'sent' && (
                <div style={{ fontSize: 15, color: '#166534', fontWeight: 700, marginBottom: 12 }}>
                  {t.emailSent} {t.checkInComplete}
                </div>
              )}

              {submitBlock.error && (
                <div
                  style={{
                    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
                    padding: '10px 12px', marginBottom: 12, color: '#991b1b',
                    fontSize: 14, fontWeight: 700, wordBreak: 'break-word',
                  }}
                >
                  {submitBlock.error}
                </div>
              )}

              {submitBlock.kind === 'waiting' && (
                <div
                  style={{
                    background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10,
                    padding: '10px 12px', marginBottom: 12, color: '#92400e',
                    fontSize: 14, fontWeight: 700,
                  }}
                >
                  🔄 {lang === 'el'
                    ? 'Θα ξαναδοκιμάσω ΜΟΝΟ ΜΟΥ μόλις επανέλθει το δίκτυο. Μην κλείσεις την εφαρμογή.'
                    : 'I will retry automatically as soon as the network is back. Do not close the app.'}
                </div>
              )}

              {submitBlock.blockers && submitBlock.blockers.length > 0 && (
                <>
                  <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 8, color: '#111827' }}>
                    {lang === 'el' ? 'Τι λείπει:' : 'What is missing:'}
                  </div>
                  {submitBlock.blockers.map((b: any, i: number) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        border: '1px solid #fecaca', background: '#fef2f2',
                        borderRadius: 10, padding: '10px 12px', marginBottom: 8,
                      }}
                    >
                      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                        <div style={{ fontWeight: 800, color: '#991b1b', fontSize: 14 }}>
                          • {b.text}
                        </div>
                        {b.detail && (
                          <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 3 }}>{b.detail}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSubmitBlock(null);
                          if (b.page && b.page < 5) {
                            navigatePage5('/page' + b.page);
                            return;
                          }
                          const refMap: any = {
                            skipper: skipperSignatureRef,
                            employee: employeeSignatureRef,
                            payment: paymentAuthRef,
                          };
                          const r = b.ref ? refMap[b.ref] : null;
                          if (r && r.current) {
                            r.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }}
                        style={{
                          background: '#dc2626', color: '#fff', border: 'none',
                          borderRadius: 8, padding: '10px 16px', fontWeight: 800,
                          fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        {lang === 'el' ? '➜ Πήγαινέ με εκεί' : '➜ Take me there'}
                      </button>
                    </div>
                  ))}
                </>
              )}

              {submitBlock.warnings && submitBlock.warnings.length > 0 && (
                <>
                  <div style={{ fontWeight: 800, fontSize: 15, margin: '14px 0 8px', color: '#92400e' }}>
                    {lang === 'el' ? 'Προειδοποιήσεις:' : 'Warnings:'}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 20, color: '#92400e', fontSize: 13 }}>
                    {submitBlock.warnings.map((w: string, i: number) => (
                      <li key={i} style={{ marginBottom: 4 }}>{w}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div style={{ padding: '0 20px 20px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {submitBlock.kind !== 'sent' && (
                <button
                  type="button"
                  onClick={() => { setSubmitBlock(null); handleSubmit(); }}
                  disabled={isSubmitting}
                  style={{
                    flex: '1 1 180px', background: '#dc2626', color: '#fff',
                    border: 'none', borderRadius: 10, padding: '14px 18px',
                    fontWeight: 900, fontSize: 16, cursor: 'pointer',
                  }}
                >
                  🔁 {lang === 'el' ? 'Δοκίμασε ξανά' : 'Try again'}
                </button>
              )}
              <button
                type="button"
                onClick={() => setSubmitBlock(null)}
                style={{
                  flex: '1 1 140px',
                  background: submitBlock.kind === 'sent' ? '#16a34a' : '#374151',
                  color: '#fff', border: 'none', borderRadius: 10,
                  padding: '14px 18px', fontWeight: 800, fontSize: 16, cursor: 'pointer',
                }}
              >
                {lang === 'el' ? 'Κλείσιμο' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Chat Widget */}
    </div>
  );
}

// =================================================================
// END OF PART 4 - THE COMPLETE PAGE 5!
// =================================================================
// TO COMBINE ALL PARTS INTO ONE FILE:
// 1. Create new Page5.jsx
// 2. Copy PART 1 (imports, constants, helpers)
// 3. Copy PART 2 (UI components)
// 4. Copy PART 3 (main component + state + handlers)
// 5. Copy PART 4 (JSX render)
// =================================================================
// ✅ ALL FIXES APPLIED:
// - Single Skipper Signature (removed duplicate)
// - Employee Login Box (yellow → green)
// - Employee Signature Lock
// - PDF & Submit Button Locks
// - Payment Authorization WITHOUT signature box
// - All syntax errors fixed
// =================================================================
// DEMO CODES: ADMIN2024, EMP001, EMP002, VIEW123
// =================================================================